import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft } from "lucide-react";
import { Capacitor } from '@capacitor/core';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { App as CapApp } from '@capacitor/app';
import axios from "axios";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { deviceTimezone } from "@/lib/deviceTimezone";
import { useUser } from "@/contexts/UserContext";
import { trackOnboardingStep } from "@/lib/analytics";
import { setAnalyticsConsent } from "@/lib/firebaseAnalytics";
import Lottie from 'lottie-react';
import forestAnimation from '@/res/forest.json';
import dashboardPortrait from '@/res/dash-portrait.png';
import IsometricGarden from '@/components/IsometricGarden';
import { MATURE_TREES, createInitialGarden } from '@/components/experimental/isometricGardenModel';
import sparkleSfx from '@/res/sparkle-sound-effect.mp4';
import { useInAppPurchase } from '@/hooks/useInAppPurchase';
import { useStripePrices } from '@/hooks/useStripePrices';
import SocialProof from '@/components/SocialProof';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Full grass garden for the welcome interaction (matches Dashboard)
const makeWelcomeGrid = (matureTile) => {
  const g = createInitialGarden(); // 8×8 all grass
  if (matureTile && matureTile !== 'EMPTY') {
    g[7][7] = matureTile;
  }
  return g;
};

/* ─── Reusable UI ──────────────────────────────────────────────── */

const OptionBtn = ({ selected, onClick, children, className = '' }) => (
  <button
    onClick={onClick}
    className={`w-full h-[52px] px-5 text-[15px] font-medium text-center transition-all ${
      selected
        ? 'bg-black text-white ring-2 ring-black'
        : 'bg-[#F4EFE8] text-[#1A211E] border border-[#D9CCC4] hover:border-[#B8C6B0]'
    } ${className}`}
    style={{ borderRadius: '10px' }}
  >
    {children}
  </button>
);

const AnimatedCounter = ({ target, suffix = '%', trigger = true, decimals = 0 }) => {
  const [count, setCount] = React.useState(0);
  React.useEffect(() => {
    if (!trigger) { setCount(0); return; }
    let start = 0;
    const step = target / 125;
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setCount(target); clearInterval(timer); }
      else setCount(decimals > 0 ? parseFloat(start.toFixed(decimals)) : Math.round(start));
    }, 16);
    return () => clearInterval(timer);
  }, [target, trigger, decimals]);
  const display = decimals > 0 ? Number(count).toFixed(decimals) : count;
  return <span>{display}{suffix}</span>;
};

/* ═══ Hold-to-Complete Button helpers (mirrors Dashboard) ═══ */
const HOLD_DURATION = 2000;
const HOLD_COLORS = ['#4CD964','#7BE08A','#A8ECB4','#EF9F27','#ED93B1'];
const BEEP_SCHEDULE = [
  { p: 0.20, freq: 280, vol: 0.07, dur: 0.08 },
  { p: 0.35, freq: 320, vol: 0.09, dur: 0.08 },
  { p: 0.50, freq: 380, vol: 0.11, dur: 0.09 },
  { p: 0.63, freq: 440, vol: 0.13, dur: 0.09 },
  { p: 0.74, freq: 520, vol: 0.15, dur: 0.10 },
  { p: 0.83, freq: 600, vol: 0.17, dur: 0.10 },
  { p: 0.90, freq: 700, vol: 0.19, dur: 0.11 },
  { p: 0.95, freq: 820, vol: 0.21, dur: 0.11 },
  { p: 0.98, freq: 980, vol: 0.23, dur: 0.12 },
];

let holdAudioCtx = null;
function getHoldAudio() {
  if (!holdAudioCtx) holdAudioCtx = new (window.AudioContext || window.webkitAudioContext)();
  return holdAudioCtx;
}
function playBip(freq, vol, duration) {
  try {
    const ac = getHoldAudio();
    const osc = ac.createOscillator();
    const gain = ac.createGain();
    const now = ac.currentTime;
    osc.type = 'sine';
    osc.frequency.setValueAtTime(freq, now);
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(vol, now + 0.008);
    gain.gain.exponentialRampToValueAtTime(0.001, now + duration);
    osc.connect(gain); gain.connect(ac.destination);
    osc.start(now); osc.stop(now + duration + 0.01);
  } catch (_) {}
}
function playCompleteSound() {
  try {
    const audio = new Audio(sparkleSfx);
    audio.volume = 0.5;
    audio.play().catch(() => {});
  } catch (_) {}
}

const HoldToCompleteButton = ({ onComplete, label }) => {
  const [holdDone, setHoldDone] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);
  const [progress, setProgress] = useState(0);
  const holdingRef = useRef(false);
  const rafRef = useRef(null);
  const pulseIntervalRef = useRef(null);
  const bipsPlayedRef = useRef(new Array(BEEP_SCHEDULE.length).fill(false));
  const btnRef = useRef(null);
  const canvasRef = useRef(null);
  const particlesRef = useRef([]);
  const particleLoopRef = useRef(false);
  const [pulseClass, setPulseClass] = useState('');

  const complete = useCallback(() => {
    if (holdDone) return;
    setHoldDone(true);
    setIsCompleting(true);
    setProgress(1);
    const btn = btnRef.current;
    if (btn) {
      btn.style.background = '#4CD964';
      btn.style.width = '100px';
      btn.style.height = '100px';
      btn.style.borderRadius = '999px';
      btn.style.transform = '';
    }
    // burst particles
    const canvas = canvasRef.current;
    if (canvas) {
      const cx = canvas.width / 2, cy = canvas.height / 2;
      for (let i = 0; i < 45; i++) {
        const angle = Math.random() * Math.PI * 2;
        const speed = 3 + Math.random() * 5;
        particlesRef.current.push({ x: cx, y: cy, vx: Math.cos(angle) * speed, vy: Math.sin(angle) * speed, life: 1, decay: 0.016 + Math.random() * 0.018, r: 3 + Math.random() * 4, color: ['#4CD964','#34C759','#A8E6CF','#FFD93D','#FF6B6B'][Math.floor(Math.random() * 5)], gravity: 0.07 });
      }
      if (!particleLoopRef.current) {
        particleLoopRef.current = true;
        const loop = () => {
          const ctx = canvas.getContext('2d');
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          particlesRef.current = particlesRef.current.filter(p => p.life > 0);
          particlesRef.current.forEach(p => { p.x += p.vx; p.y += p.vy; p.vy += p.gravity; p.vx *= 0.97; p.life -= p.decay; if (p.life > 0) { ctx.save(); ctx.globalAlpha = p.life; ctx.fillStyle = p.color; ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2); ctx.fill(); ctx.restore(); } });
          if (particlesRef.current.length > 0) requestAnimationFrame(loop);
          else particleLoopRef.current = false;
        };
        requestAnimationFrame(loop);
      }
    }
    playCompleteSound();
    try { Haptics.impact({ style: ImpactStyle.Medium }); } catch (_) {}
    setTimeout(() => {
      setIsCompleting(false);
      onComplete();
    }, 250);
  }, [holdDone, onComplete]);

  // Pulse every 3s like Dashboard
  useEffect(() => {
    if (holdDone) return;
    const id = setInterval(() => {
      setPulseClass('hold-btn-pulse');
      setTimeout(() => setPulseClass(''), 1200);
    }, 3000);
    return () => clearInterval(id);
  }, [holdDone]);

  // lerp helpers matching Dashboard
  const lerpVal = (a, b, t) => a + (b - a) * t;
  const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
  const lerpColor = (c1, c2, t) => {
    const h = s => parseInt(s.substring(1), 16);
    const r1 = (h(c1) >> 16) & 255, g1 = (h(c1) >> 8) & 255, b1 = h(c1) & 255;
    const r2 = (h(c2) >> 16) & 255, g2 = (h(c2) >> 8) & 255, b2 = h(c2) & 255;
    const r = Math.round(r1 + (r2 - r1) * t), g = Math.round(g1 + (g2 - g1) * t), b = Math.round(b1 + (b2 - b1) * t);
    return `rgb(${r},${g},${b})`;
  };

  const setProgressStyle = useCallback((p) => {
    const btn = btnRef.current;
    if (!btn) return;
    const size = lerpVal(100, 130, easeOutCubic(p));
    btn.style.width = size + 'px';
    btn.style.height = size + 'px';
    btn.style.borderRadius = '999px';
    btn.style.background = lerpColor('#5C8748', '#4CD964', easeOutCubic(p));
    const shake = p > 0.8 ? Math.sin(Date.now() / 40) * 2.5 * ((p - 0.8) / 0.2) : 0;
    btn.style.transform = `translateX(${shake}px)`;
  }, []);

  // Override startHold to use ref-based progress like Dashboard
  const startHoldDash = useCallback((e) => {
    if (holdDone || isCompleting) return;
    try { getHoldAudio(); } catch (_) {}
    holdingRef.current = true;
    bipsPlayedRef.current = new Array(BEEP_SCHEDULE.length).fill(false);
    const startTime = performance.now();
    pulseIntervalRef.current = setInterval(() => {
      if (!holdingRef.current) { clearInterval(pulseIntervalRef.current); return; }
      // spawn canvas particles
      const canvas = canvasRef.current;
      if (canvas) {
        const cx = canvas.width / 2, cy = canvas.height / 2;
        for (let i = 0; i < 2; i++) {
          const angle = Math.random() * Math.PI * 2;
          const speed = 1 + Math.random() * 2;
          particlesRef.current.push({ x: cx, y: cy, vx: Math.cos(angle) * speed, vy: Math.sin(angle) * speed, life: 1, decay: 0.03 + Math.random() * 0.02, r: 2 + Math.random() * 2, color: ['#4CD964','#34C759','#A8E6CF','#FFD93D','#FF6B6B'][Math.floor(Math.random() * 5)], gravity: 0.07 });
        }
        if (!particleLoopRef.current) {
          particleLoopRef.current = true;
          const loop = () => {
            const ctx = canvas.getContext('2d');
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            particlesRef.current = particlesRef.current.filter(p => p.life > 0);
            particlesRef.current.forEach(p => { p.x += p.vx; p.y += p.vy; p.vy += p.gravity; p.vx *= 0.97; p.life -= p.decay; if (p.life > 0) { ctx.save(); ctx.globalAlpha = p.life; ctx.fillStyle = p.color; ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2); ctx.fill(); ctx.restore(); } });
            if (particlesRef.current.length > 0) requestAnimationFrame(loop);
            else particleLoopRef.current = false;
          };
          requestAnimationFrame(loop);
        }
      }
    }, 130);
    const tick = (now) => {
      if (!holdingRef.current || holdDone) return;
      const p = Math.min((now - startTime) / HOLD_DURATION, 1);
      setProgress(p);
      setProgressStyle(p);
      BEEP_SCHEDULE.forEach((b, idx) => {
        if (p >= b.p && !bipsPlayedRef.current[idx]) {
          bipsPlayedRef.current[idx] = true;
          playBip(b.freq, b.vol, b.dur);
          // Haptic pulse synced with each beep as the button expands.
          // Stronger feedback as we approach completion.
          try {
            const style = b.p >= 0.8
              ? ImpactStyle.Medium
              : ImpactStyle.Light;
            Haptics.impact({ style });
          } catch (_) {}
        }
      });
      if (p < 1) rafRef.current = requestAnimationFrame(tick);
      else complete();
    };
    rafRef.current = requestAnimationFrame(tick);
  }, [complete, holdDone, isCompleting, setProgressStyle]);

  const stopHoldDash = useCallback(() => {
    if (holdDone) return;
    holdingRef.current = false;
    clearInterval(pulseIntervalRef.current);
    cancelAnimationFrame(rafRef.current);
    const btn = btnRef.current;
    if (!btn) { setProgress(0); return; }
    const cur = parseFloat(btn.style.width || 100);
    const from = (cur - 100) / (130 - 100);
    const start = performance.now();
    const snap = (now) => {
      const t = Math.min((now - start) / 350, 1);
      setProgressStyle(from * (1 - easeOutCubic(t)));
      if (t < 1) requestAnimationFrame(snap);
      else { btn.style.transform = ''; }
    };
    requestAnimationFrame(snap);
    setProgress(0);
  }, [holdDone, setProgressStyle]);

  useEffect(() => {
    const stop = () => stopHoldDash();
    window.addEventListener('mouseup', stop);
    window.addEventListener('touchend', stop);
    return () => {
      window.removeEventListener('mouseup', stop);
      window.removeEventListener('touchend', stop);
      clearInterval(pulseIntervalRef.current);
      cancelAnimationFrame(rafRef.current);
    };
  }, [stopHoldDash]);

  return (
    <>
      <style>{`
        @keyframes holdBtnPulse {
          0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(126,168,108,0.25); }
          50% { transform: scale(1.04); box-shadow: 0 0 24px 8px rgba(126,168,108,0.15); }
        }
        .hold-btn-pulse { animation: holdBtnPulse 1.2s ease-in-out; }
        @keyframes holdHintRing {
          0%   { transform: scale(0.85); opacity: 0.55; }
          70%  { transform: scale(1.35); opacity: 0;    }
          100% { transform: scale(1.35); opacity: 0;    }
        }
        .hold-hint-ring {
          position: absolute; border-radius: 999px;
          border: 2px solid #7EA86C; pointer-events: none;
          animation: holdHintRing 1.8s ease-out infinite;
        }
        .hold-hint-ring.delay { animation-delay: 0.6s; }
        @keyframes holdHintFinger {
          0%, 100% { transform: translateY(0) scale(1); }
          50%      { transform: translateY(4px) scale(0.92); }
        }
        .hold-hint-finger { display: inline-block; animation: holdHintFinger 1s ease-in-out infinite; }
        @keyframes holdHintPillFade {
          0%, 100% { opacity: 0.92; transform: translateY(0); }
          50%      { opacity: 1;    transform: translateY(-2px); }
        }
        .hold-hint-pill { animation: holdHintPillFade 2s ease-in-out infinite; }
        .hold-hint-pill::after {
          content: '';
          position: absolute;
          bottom: -8px;
          left: 50%;
          transform: translateX(-50%) rotate(45deg);
          width: 14px;
          height: 14px;
          background: #FFFFFF;
          border-right: 1px solid rgba(126,168,108,0.35);
          border-bottom: 1px solid rgba(126,168,108,0.35);
        }
      `}</style>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '8px 0 8px' }}>
        {!holdDone && (
          <div
            className="hold-hint-pill"
            style={{
              position: 'relative',
              marginBottom: 14,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              padding: '8px 14px',
              borderRadius: 14,
              background: '#FFFFFF',
              border: '1px solid rgba(126,168,108,0.35)',
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              color: '#3d6b3d',
              fontFamily: 'Manrope-Variable',
              fontSize: 13,
              fontWeight: 600,
              letterSpacing: '0.02em',
              zIndex: 4,
            }}
          >
            <span className="hold-hint-finger" role="img" aria-label="hold" style={{ fontSize: 16 }}>👇</span>
            <span>
              {label === 'Fatto' || label === 'Lo farò'
                ? 'Tieni premuto per completare'
                : label === 'Lo haré' || label === 'Hecho'
                  ? 'Mantén pulsado para completar'
                  : 'Hold to complete'}
            </span>
          </div>
        )}
        <div style={{ position: 'relative', width: 180, height: 180, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <canvas ref={canvasRef} width={180} height={180} style={{ position: 'absolute', top: 0, left: 0, width: 180, height: 180, pointerEvents: 'none' }} />
          {!holdDone && !isCompleting && (
            <>
              <span className="hold-hint-ring" style={{ width: 100, height: 100 }} />
              <span className="hold-hint-ring delay" style={{ width: 100, height: 100 }} />
            </>
          )}
          <button
            ref={btnRef}
            onMouseDown={startHoldDash}
            onTouchStart={startHoldDash}
            disabled={isCompleting || holdDone}
            className={`hold-to-complete ${pulseClass}`}
            style={{
              width: 100, height: 100, borderRadius: '999px',
              border: 'none', background: holdDone ? '#4CD964' : '#5C8748',
              cursor: isCompleting || holdDone ? 'default' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              position: 'relative', zIndex: 3, touchAction: 'none',
              userSelect: 'none', WebkitUserSelect: 'none',
            }}
          >
            {holdDone ? (
              <span style={{ fontSize: 28, pointerEvents: 'none' }} role="img" aria-label="seed">🌱</span>
            ) : (
              <span style={{ fontFamily: 'Manrope-Variable', fontSize: 15, fontWeight: 700, color: '#fff', pointerEvents: 'none', letterSpacing: '0.02em' }}>
                {label}
              </span>
            )}
          </button>
        </div>
      </div>
    </>
  );
};

/* ─── Empathy Variant Engine ───────────────────────────────────── */
// TODO: VERIFY — All statistics below are cited from published research.
// Cross-check exact figures against the original publications before shipping.

function getEmpathyVariant(formData, lang) {
  const { motivation, has_kids, pain_autopilot, pain_energy, pain_gestures, pain_connection, relationship_status } = formData;
  const isIt = lang === 'it';
  const isEs = lang === 'es';
  const withKids = has_kids === 'yes';

  // Determine dominant pain point from the 4 scales
  const painScores = { never: 0, rarely: 1, sometimes: 2, often: 3, very_often: 4 };
  const connectionScores = { very_connected: 0, connected: 1, neutral: 2, distant: 3, very_distant: 4 };
  const scores = {
    autopilot: painScores[pain_autopilot] ?? 0,
    energy: painScores[pain_energy] ?? 0,
    gestures: painScores[pain_gestures] ?? 0,
    connection: connectionScores[pain_connection] ?? 0,
  };
  const totalPain = scores.autopilot + scores.energy + scores.gestures + scores.connection;
  const dominantPain = Object.entries(scores).sort((a, b) => b[1] - a[1])[0][0];
  const isHighPain = totalPain >= 10; // avg 2.5+ out of 4

  // Body lines deeply adapt to pain profile
  let bodyLines;
  if (dominantPain === 'autopilot' && scores.autopilot >= 3) {
    bodyLines = withKids
      ? (isIt
          ? 'Tra lavoro e figli, è facile andare in automatico.\nFai tanto, ma senti che manca qualcosa.\nNon sei il solo.'
          : isEs
          ? 'Entre el trabajo y los hijos, es fácil ir en piloto automático.\nHaces mucho, pero sientes que algo falta.\nNo eres el único.'
          : 'Between work and kids, it\'s easy to go on autopilot.\nYou do a lot, but something feels missing.\nYou\'re not alone.')
      : (isIt
          ? 'La routine prende il sopravvento.\nFai le cose, ma senza essere davvero presente.\nSuccede a molti.'
          : isEs
          ? 'La rutina se apodera de todo.\nHaces las cosas, pero sin estar realmente presente.\nLe pasa a muchos.'
          : 'Routine takes over.\nYou go through the motions without being truly present.\nIt happens to many.');
  } else if (dominantPain === 'energy' && scores.energy >= 3) {
    bodyLines = withKids
      ? (isIt
          ? 'Lavoro, figli, casa.\nA fine giornata l\'energia è finita.\nÈ una realtà che molti padri conoscono bene.'
          : isEs
          ? 'Trabajo, hijos, casa.\nAl final del día no queda nada.\nEs una realidad que muchos padres conocen bien.'
          : 'Work, kids, home.\nBy the end of the day, there\'s nothing left.\nIt\'s a reality many fathers know well.')
      : (isIt
          ? 'Tra lavoro e impegni, sei prosciugato.\nVorresti fare di più, ma il corpo dice basta.\nÈ più comune di quanto pensi.'
          : isEs
          ? 'Entre el trabajo y las obligaciones, estás agotado.\nQuisieras hacer más, pero el cuerpo dice basta.\nEs más común de lo que crees.'
          : 'Between work and commitments, you\'re drained.\nYou want to do more, but your body says stop.\nIt\'s more common than you think.');
  } else if (dominantPain === 'gestures' && scores.gestures >= 3) {
    bodyLines = isIt
      ? 'I piccoli gesti scivolano via.\nUn grazie non detto, un aiuto mancato.\nNon è mancanza di amore — è la vita che corre.'
      : isEs
      ? 'Los pequeños gestos se escapan.\nUn gracias no dicho, una ayuda que faltó.\nNo es falta de amor — es la vida que corre.'
      : 'Small gestures slip away.\nAn unsaid thank you, a missed moment to help.\nIt\'s not lack of love — life just moves fast.';
  } else if (dominantPain === 'connection' && scores.connection >= 3) {
    bodyLines = isIt
      ? 'Ti senti distante dalla tua partner.\nVivete insieme, ma vi manca la connessione.\nÈ un sentimento più diffuso di quanto credi.'
      : isEs
      ? 'Te sientes distante de tu pareja.\nViven juntos, pero les falta la conexión.\nEs un sentimiento más común de lo que crees.'
      : 'You feel distant from your partner.\nYou live together, but the connection is missing.\nIt\'s more common than you\'d think.';
  } else if (withKids) {
    bodyLines = isIt
      ? 'La vita è piena.\nLavoro, figli, responsabilità.\nMolti partner sentono la stessa tensione.'
      : isEs
      ? 'La vida está llena.\nTrabajo, hijos, responsabilidades.\nMuchas parejas sienten la misma tensión.'
      : 'Life gets busy.\nWork, kids, responsibilities.\nMany partners feel the same tension.';
  } else {
    bodyLines = isIt
      ? 'La vita è piena.\nLavoro, impegni, responsabilità.\nMolti partner sentono la stessa tensione.'
      : isEs
      ? 'La vida está llena.\nTrabajo, obligaciones, responsabilidades.\nMuchas parejas sienten la misma tensión.'
      : 'Life gets busy.\nWork, commitments, responsibilities.\nMany partners feel the same tension.';
  }

  // Closing line adapts to motivation
  let closingLine;
  if (motivation === 'reconnect') {
    closingLine = isIt ? 'Il primo passo è già voler cambiare.' : isEs ? 'Querer cambiar ya es el primer paso.' : 'Wanting to change is already the first step.';
  } else if (motivation === 'improve_relationship') {
    closingLine = isIt ? 'Ogni piccolo gesto fa la differenza.' : isEs ? 'Cada pequeño gesto hace la diferencia.' : 'Every small gesture makes a difference.';
  } else {
    closingLine = isIt ? 'Sei più vicino di quanto pensi.' : isEs ? 'Estás más cerca de lo que crees.' : "You're closer than you think.";
  }

  // Stat variants: dominant pain × motivation × has_kids
  // Priority: match dominant pain point first, then refine with context

  // AUTOPILOT dominant — feeling disconnected from daily life
  if (dominantPain === 'autopilot' && scores.autopilot >= 3) {
    if (withKids) {
      // TODO: VERIFY — Pew Research Center, 2015 — "Raising Kids and Running a Household"
      return {
        bodyLines, closingLine,
        statNumber: 46, statSuffix: '%', decimals: 0,
        statDescription: isIt
          ? 'dei padri sente di non passare abbastanza tempo di qualità con la famiglia.'
          : isEs
          ? 'de los padres siente que no pasa suficiente tiempo de calidad con su familia.'
          : 'of fathers feel they don\'t spend enough quality time with their family.',
        source: 'Pew Research Center, 2015',
      };
    }
    // TODO: VERIFY — Gallup, 2023 — "State of the Global Workplace"
    return {
      bodyLines, closingLine,
      statNumber: 59, statSuffix: '%', decimals: 0,
      statDescription: isIt
        ? 'dei lavoratori si sente emotivamente distaccato dalla propria vita quotidiana.'
        : isEs
        ? 'de los trabajadores se siente emocionalmente desconectado de su vida diaria.'
        : 'of workers feel emotionally detached from their daily life.',
      source: 'Gallup, 2023',
    };
  }

  // ENERGY dominant — too exhausted to show up
  if (dominantPain === 'energy' && scores.energy >= 3) {
    if (withKids) {
      // TODO: VERIFY — APA, 2023 — "Stress in America"
      return {
        bodyLines, closingLine,
        statNumber: 48, statSuffix: '%', decimals: 0,
        statDescription: isIt
          ? 'dei genitori dichiara di essere troppo stanco per interagire con il partner a fine giornata.'
          : isEs
          ? 'de los padres dice estar demasiado cansado para interactuar con su pareja al final del día.'
          : 'of parents say they\'re too tired to engage with their partner at the end of the day.',
        source: 'APA, 2023 — Stress in America',
      };
    }
    // TODO: VERIFY — APA, 2023 — "Stress in America"
    return {
      bodyLines, closingLine,
      statNumber: 76, statSuffix: '%', decimals: 0,
      statDescription: isIt
        ? 'degli adulti dice che lo stress influisce significativamente sulle proprie relazioni.'
        : isEs
        ? 'de los adultos dice que el estrés afecta significativamente sus relaciones personales.'
        : 'of adults say stress significantly impacts their personal relationships.',
      source: 'APA, 2023 — Stress in America',
    };
  }

  // GESTURES dominant — small gestures slipping
  if (dominantPain === 'gestures' && scores.gestures >= 3) {
    // TODO: VERIFY — Gottman Institute, 2019 — "The Relationship Cure"
    return {
      bodyLines, closingLine,
      statNumber: 86, statSuffix: '%', decimals: 0,
      statDescription: isIt
        ? 'delle coppie felici risponde ai piccoli gesti quotidiani del partner.'
        : isEs
        ? 'de las parejas felices responde a los pequeños gestos cotidianos de su pareja.'
        : 'of happy couples respond to their partner\'s small everyday bids for connection.',
      source: 'Gottman Institute, 2019',
    };
  }

  // CONNECTION dominant — feeling distant
  if (dominantPain === 'connection' && scores.connection >= 3) {
    if (motivation === 'reconnect' || motivation === 'improve_relationship') {
      // TODO: VERIFY — National Marriage Project, UVA, 2012
      return {
        bodyLines, closingLine,
        statNumber: 3.5, statSuffix: '×', decimals: 1,
        statDescription: isIt
          ? 'più probabilità di essere molto felici per le coppie che investono in tempo di qualità intenzionale.'
          : isEs
          ? 'más probabilidades de ser muy felices para las parejas que invierten en tiempo de calidad intencional.'
          : 'more likely to be very happy for couples who invest in intentional quality time.',
        source: 'National Marriage Project, UVA, 2012',
      };
    }
    // TODO: VERIFY — Pew Research Center, 2023 — "Parenting in America Today"
    return {
      bodyLines, closingLine,
      statNumber: 33, statSuffix: '%', decimals: 0,
      statDescription: isIt
        ? 'delle coppie dice di sentirsi meno connessa rispetto al primo anno insieme.'
        : isEs
        ? 'de las parejas dice sentirse menos conectada que en su primer año juntos.'
        : 'of couples say they feel less connected than in their first year together.',
      source: 'Pew Research Center, 2023',
    };
  }

  // HIGH OVERALL PAIN but no single dominant — general overwhelm
  if (isHighPain) {
    if (withKids) {
      // TODO: VERIFY — Pew Research Center, 2023 — "Parenting in America Today"
      return {
        bodyLines, closingLine,
        statNumber: 62, statSuffix: '%', decimals: 0,
        statDescription: isIt
          ? 'dei genitori dice che crescere figli è più difficile di quanto si aspettasse.'
          : isEs
          ? 'de los padres dice que criar hijos es más difícil de lo que esperaba.'
          : 'of parents say raising kids is harder than they expected.',
        source: 'Pew Research Center, 2023',
      };
    }
    // TODO: VERIFY — APA, 2023 — "Stress in America"
    return {
      bodyLines, closingLine,
      statNumber: 76, statSuffix: '%', decimals: 0,
      statDescription: isIt
        ? 'degli adulti dice che lo stress influisce significativamente sulle proprie relazioni.'
        : isEs
        ? 'de los adultos dice que el estrés afecta significativamente sus relaciones personales.'
        : 'of adults say stress significantly impacts their personal relationships.',
      source: 'APA, 2023 — Stress in America',
    };
  }

  // LOW PAIN / MILD — motivation-based fallbacks
  if ((motivation === 'be_present' || motivation === 'do_more') && withKids) {
    // TODO: VERIFY — Pew Research Center, 2015
    return {
      bodyLines, closingLine,
      statNumber: 46, statSuffix: '%', decimals: 0,
      statDescription: isIt
        ? 'dei padri dice di passare troppo poco tempo con i propri figli.'
        : isEs
        ? 'de los padres dice pasar muy poco tiempo con sus hijos.'
        : 'of fathers say they spend too little time with their kids.',
      source: 'Pew Research Center, 2015',
    };
  }

  if (motivation === 'be_present' && !withKids) {
    // TODO: VERIFY — Pew Research Center, 2022
    return {
      bodyLines, closingLine,
      statNumber: 50, statSuffix: '%', decimals: 0,
      statDescription: isIt
        ? 'degli adulti lavoratori trova difficile bilanciare lavoro e vita personale.'
        : isEs
        ? 'de los adultos que trabajan considera difícil equilibrar el trabajo y la vida personal.'
        : 'of working adults say balancing work and personal life is difficult.',
      source: 'Pew Research Center, 2022',
    };
  }

  // Fallback
  // TODO: VERIFY — Pew Research Center, 2022
  return {
    bodyLines, closingLine,
    statNumber: 50, statSuffix: '%', decimals: 0,
    statDescription: isIt
      ? 'degli adulti lavoratori trova difficile bilanciare lavoro e vita personale.'
      : isEs
      ? 'de los adultos que trabajan considera difícil equilibrar el trabajo y la vida personal.'
      : 'of working adults say balancing work and personal life is difficult.',
    source: 'Pew Research Center, 2022',
  };
}

/* ─── Auth transport ───────────────────────────────────────────── */

function useAuthTransport() {
  const [accessToken, setAccessToken] = useState(null);
  const [loading, setLoading] = useState(true);

  React.useEffect(() => {
    const getInitialSession = async () => {
      try {
        // Timeout getSession to avoid capacitorStorage deadlock
        const _res = await Promise.race([
          (supabase?.auth?.getSession?.() || Promise.resolve(undefined)).catch(() => undefined),
          new Promise(r => setTimeout(() => r(undefined), 3000)),
        ]);
        const session = _res?.data?.session;
        if (session?.access_token) {
          setAccessToken(session.access_token);
        } else {
          // Fallback: check localStorage (set by Login.js finalizeAndRoute)
          const stored = localStorage.getItem('bh_access_token');
          setAccessToken(stored || null);
        }
      } catch (err) {
        console.error('OnboardingV2: Error getting session:', err);
        const stored = localStorage.getItem('bh_access_token');
        setAccessToken(stored || null);
      } finally { setLoading(false); }
    };
    getInitialSession();

    const _onAuthRes = supabase?.auth?.onAuthStateChange?.(
      async (event, session) => {
        if (session?.access_token) setAccessToken(session.access_token);
        else setAccessToken(null);
        setLoading(false);
      }
    ) || { data: { subscription: { unsubscribe: () => {} } } };

    return () => _onAuthRes.data.subscription.unsubscribe();
  }, []);

  const cfg = React.useMemo(() => {
    if (accessToken) {
      return { headers: { Authorization: `Bearer ${accessToken}` }, withCredentials: false, accessToken, loading };
    }
    const fallbackHeaders = {};
    const defaultAuth = axios.defaults.headers?.common?.Authorization;
    const defaultUserId = axios.defaults.headers?.common?.['X-User-Id'] || axios.defaults.headers?.common?.['x-user-id'];
    if (defaultAuth) fallbackHeaders.Authorization = defaultAuth;
    if (defaultUserId) fallbackHeaders['X-User-Id'] = defaultUserId;
    return { headers: fallbackHeaders, withCredentials: axios.defaults.withCredentials === true, accessToken: null, loading };
  }, [accessToken, loading]);

  return cfg;
}

/* ─── Main component ───────────────────────────────────────────── */

const OnboardingV2 = ({ onComplete }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const { user, fetchUserData, updateUser } = useUser();
  const authCfg = useAuthTransport();

  const _entryParams = new URLSearchParams(location.search);
  const isPaywallEntry = _entryParams.get('step') === 'paywall';
  // `?paywall_step=` picks which paywall screen to open. Every "Subscribe" CTA
  // in the app links to paywall_step=24 (the purchase screen) — without this
  // the parameter was ignored and they all landed on 22, two taps short of
  // being able to buy anything. Clamped to the paywall range so a stale or
  // hand-edited link can't drop the user into the questionnaire.
  const PAYWALL_FIRST_STEP = 22;
  const PAYWALL_LAST_STEP = 24;
  const _requestedPaywallStep = parseInt(_entryParams.get('paywall_step'), 10);
  const initialStep = isPaywallEntry
    ? (Number.isFinite(_requestedPaywallStep)
      ? Math.min(Math.max(_requestedPaywallStep, PAYWALL_FIRST_STEP), PAYWALL_LAST_STEP)
      : PAYWALL_FIRST_STEP)
    : 0;
  const [currentStep, setCurrentStep] = useState(initialStep);
  const [saving, setSaving] = useState(false);
  const [showTransition, setShowTransition] = useState(false);
  const [empathyRevealed, setEmpathyRevealed] = useState(false);
  const [outcomeRevealed, setOutcomeRevealed] = useState(false);
  const [personalizationDone, setPersonalizationDone] = useState(false);
  // There is only one product now, so the plan is fixed rather than chosen.
  // The state is kept (rather than removed) because several effects, the
  // analytics payload and the CTA guard all read it.
  const [selectedPlan] = useState('lifetime');
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  // In-App Purchase hook (native iOS/Android)
  const iapUserId = user?.user_id || user?.id || localStorage.getItem('betterhusbandUserId');
  const { isNative: isIAP, products: iapProducts, purchasing: iapPurchasing, error: iapError, purchase: iapPurchase, restorePurchases: iapRestore, refreshProducts: iapRefresh, loading: iapLoading, PRODUCT_IDS } = useInAppPurchase(iapUserId);
  // Live Stripe price for the web paywall.
  const webPrices = useStripePrices(i18n.language?.startsWith('it') || i18n.language?.startsWith('es') ? 'eur' : 'usd');

  // Surface IAP errors as toasts
  useEffect(() => {
    if (iapError) toast.error(iapError);
  }, [iapError]);

  const [welcomeDone, setWelcomeDone] = useState(false);
  const [showContinue, setShowContinue] = useState(false);
  const [welcomeTree, setWelcomeTree] = useState(null); // null = grass only
  const [welcomeAnim, setWelcomeAnim] = useState(null);
  const [onboardingSaved, setOnboardingSaved] = useState(false);
  const [legalModal, setLegalModal] = useState(null); // null | 'terms' | 'privacy'

  // Load saved onboarding data from localStorage (pre-auth local persistence)
  const [formData, setFormData] = useState(() => {
    const defaults = {
      language: i18n.language || 'en',
      motivation: [],
      relationship_status: '',
      has_kids: '',
      kids_ages: [],
      age_cluster: '',
      referral_source: '',
      improvement_areas: [],
      work_schedule: '',
      time_budget_minutes: 0,
      accepted_terms: false,
      accepted_privacy: false,
      pain_autopilot: '',
      pain_energy: '',
      pain_gestures: '',
      pain_connection: '',
    };
    try {
      const saved = localStorage.getItem('bh_onboarding_form');
      if (saved) return { ...defaults, ...JSON.parse(saved) };
    } catch (_) {}
    return defaults;
  });

  // Persist formData to localStorage on every change (pre-auth local save)
  React.useEffect(() => {
    try {
      localStorage.setItem('bh_onboarding_form', JSON.stringify(formData));
    } catch (_) {}
  }, [formData]);

  const totalSteps = 25;
  const lang = i18n.language;
  const currency = lang === 'it' ? '€' : lang === 'es' ? '€' : '$';

  // The single price on offer, always from a live source: StoreKit / Play
  // Billing on device, Stripe on web. Null until it arrives — the paywall
  // shows a placeholder and keeps its CTA disabled rather than inventing a
  // number the user might then be charged something else for.
  const lifetimeIapProduct = iapProducts.find(p => p.period === 'lifetime');
  const lifetimePrice = isIAP
    ? (lifetimeIapProduct?.price || null)
    : (webPrices.lifetime != null ? `${currency}${webPrices.lifetime.toFixed(2)}` : null);

  const canSkip = currentStep === 15 || currentStep === 17;
  const showHeader = currentStep > 4 && currentStep < 22;

  /* ── Guard: paywall entry requires auth + beta check ─────────── */
  React.useEffect(() => {
    if (isPaywallEntry && !authCfg.loading) {
      const uid = user?.user_id || user?.id || localStorage.getItem('betterhusbandUserId');
      if (!authCfg.accessToken && !uid) {
        navigate('/login', { replace: true });
        return;
      }
      // Beta tester safety net: check before showing paywall
      (async () => {
        try {
          let headers = { ...(authCfg.headers || {}), 'Content-Type': 'application/json' };
          try {
            const { data: s } = await supabase.auth.getSession();
            if (s?.session?.access_token) headers = { Authorization: `Bearer ${s.session.access_token}`, 'Content-Type': 'application/json' };
          } catch (_) {}
          const res = await axios.get(`${API}/auth/check-beta`, { headers });
          if (res.data?.is_beta) {
            await fetchUserData();
            navigate('/dashboard', { replace: true });
          }
        } catch (e) {
        }
      })();
    } else if (isPaywallEntry && authCfg.loading) {
    }
  }, [isPaywallEntry, authCfg.loading, authCfg.accessToken, authCfg.headers, user, navigate, fetchUserData]);

  /* ── Intercept hardware/browser back on paywall steps ─────────── */
  useEffect(() => {
    if (currentStep < 22) return;

    // Push a dummy history entry so the back button doesn't leave the page
    window.history.pushState({ paywall: true }, '');

    const onPopState = () => {
      // Stay on first paywall step instead of navigating away
      setCurrentStep(22);
      // Re-push so repeated back presses keep staying here
      window.history.pushState({ paywall: true }, '');
    };

    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [currentStep >= 22]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Auto-leave paywall as soon as backend reports an active sub ──
   *
   * Apple Guideline 2.1(b): the App Store reviewer reported that after a
   * successful purchase the app stayed on the IAP screen. Causes seen in
   * the wild on iPadOS 26 sandbox:
   *   • IAPTransactionObserver verified the receipt before the paywall's
   *     `purchaseResolverRef` could attach (so iapPurchase() never resolves).
   *   • StoreKit 2 returned an empty `appStoreReceipt`, which the hook
   *     interprets as "no receipt" even though the backend got the receipt
   *     via a different path (server notification / replay).
   *   • The 30s resolver timeout fires while the subscription is already
   *     active server-side, leaving the user stuck.
   *
   * Polling /subscription/status as a safety net guarantees we leave the
   * paywall the moment the backend confirms the subscription, regardless
   * of which client-side path delivered the receipt. */
  useEffect(() => {
    if (currentStep < 22) return;
    const uid = user?.user_id || user?.id || localStorage.getItem('betterhusbandUserId');
    if (!uid) return;

    let cancelled = false;
    let timer = null;

    const isActive = (status) => status === 'premium' || status === 'trial';

    const checkStatus = async () => {
      try {
        const { data } = await axios.get(`${API}/subscription/status/${uid}`);
        if (cancelled) return;
        if (isActive(data?.subscription_status)) {
          try { await fetchUserData(); } catch (_) { /* ignore */ }
          if (!cancelled) navigate('/dashboard', { replace: true });
        }
      } catch (_) { /* transient — keep polling */ }
    };

    // Poll every 3s while the user remains on the paywall.
    const schedule = () => {
      timer = setTimeout(async () => {
        await checkStatus();
        if (!cancelled) schedule();
      }, 3000);
    };
    // Immediate check + on tab/app foreground (sandbox sheet dismiss).
    checkStatus();
    schedule();
    const onVisible = () => { if (document.visibilityState === 'visible') checkStatus(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', checkStatus);

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', checkStatus);
    };
  }, [currentStep >= 22, user?.user_id, user?.id, fetchUserData, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Close legal modal on hardware back button ─────────────────── */
  useEffect(() => {
    if (!legalModal) return;
    const listener = CapApp.addListener('backButton', () => {
      setLegalModal(null);
    });
    return () => { listener.then(h => h.remove()); };
  }, [legalModal]);

  /* ── Onboarding step tracking ─────────────────────────────────── */
  const STEP_NAMES = [
    'welcome', 'landing_questions', 'landing_carry', 'landing_matters', 'landing_perfect',
    'language', 'motivation', 'relationship', 'kids',
    'pain_autopilot', 'pain_energy', 'pain_gestures', 'pain_connection',
    'age', 'empathy', 'referral', 'improvements', 'work_schedule',
    'time_budget', 'outcome', 'terms', 'personalizing',
    'paywall_try_free', 'paywall_reminder', 'paywall_trial',
  ];

  const getStepTrackingData = React.useCallback(() => {
    const persistedAnonId = typeof window !== 'undefined'
      ? localStorage.getItem('bh_anon_tracking_id')
      : null;
    const base = {
      language: formData.language,
      anonymous_id: persistedAnonId || undefined,
    };

    switch (currentStep) {
      case 6:
        return {
          ...base,
          motivations: formData.motivation,
          motivation_count: formData.motivation.length,
        };
      case 7:
        return { ...base, relationship_status: formData.relationship_status };
      case 8:
        return {
          ...base,
          has_kids: formData.has_kids,
          kids_ages: formData.kids_ages,
          kids_ages_count: formData.kids_ages.length,
        };
      case 9:
        return { ...base, pain_autopilot: formData.pain_autopilot };
      case 10:
        return { ...base, pain_energy: formData.pain_energy };
      case 11:
        return { ...base, pain_gestures: formData.pain_gestures };
      case 12:
        return { ...base, pain_connection: formData.pain_connection };
      case 13:
        return { ...base, age_cluster: formData.age_cluster };
      case 15:
        return { ...base, referral_source: formData.referral_source };
      case 16:
        return {
          ...base,
          improvement_areas: formData.improvement_areas,
          improvement_areas_count: formData.improvement_areas.length,
        };
      case 17:
        return { ...base, work_schedule: formData.work_schedule };
      case 18:
        return { ...base, time_budget_minutes: formData.time_budget_minutes };
      case 20:
        return {
          ...base,
          accepted_terms: formData.accepted_terms,
          accepted_privacy: formData.accepted_privacy,
        };
      case 22:
      case 23:
      case 24:
        return {
          ...base,
          selected_plan: selectedPlan,
          iap_native: isIAP,
          iap_loading: iapLoading,
          iap_products_loaded: iapProducts.length,
        };
      default:
        return base;
    }
  }, [currentStep, formData, selectedPlan, isIAP, iapLoading, iapProducts.length]);

  React.useEffect(() => {
    // Use real user ID if authenticated, otherwise a stable anonymous ID
    let uid = user?.user_id || user?.id;
    if (!uid) {
      let anonId = localStorage.getItem('bh_anon_tracking_id');
      if (!anonId) {
        anonId = 'anon_' + Math.random().toString(36).substring(2, 15) + Date.now().toString(36);
        localStorage.setItem('bh_anon_tracking_id', anonId);
      }
      uid = anonId;
    }
    trackOnboardingStep(uid, {
      step: currentStep,
      stepName: STEP_NAMES[currentStep] || `step_${currentStep}`,
      ageCluster: formData.age_cluster || undefined,
      stepData: getStepTrackingData(),
    });
  }, [currentStep, user, formData.age_cluster, getStepTrackingData]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Timed reveals ────────────────────────────────────────────── */
  React.useEffect(() => {
    if (currentStep === 14) {
      setEmpathyRevealed(true);
    }
  }, [currentStep]);

  React.useEffect(() => {
    if (currentStep === 19) {
      setOutcomeRevealed(false);
      const timer = setTimeout(() => setOutcomeRevealed(true), 2000);
      return () => clearTimeout(timer);
    }
  }, [currentStep]);

  React.useEffect(() => {
    if (currentStep === 21) {
      setPersonalizationDone(false);
      const timer = setTimeout(() => setPersonalizationDone(true), 8000);
      return () => clearTimeout(timer);
    }
  }, [currentStep]);



  React.useEffect(() => {
    setFormData(prev => ({ ...prev, language: i18n.language || 'en' }));
  }, [i18n.language]);

  /* ── Validation ───────────────────────────────────────────────── */
  const isStepValid = () => {
    switch (currentStep) {
      case 0: return true;
      case 1: return true;
      case 2: return true;
      case 3: return true;
      case 4: return true;
      case 5: return true;
      case 6: return formData.motivation.length > 0;
      case 7: return formData.relationship_status !== '';
      case 8: return formData.has_kids !== '' && (formData.has_kids === 'no' || formData.kids_ages.length > 0);
      case 9: return formData.pain_autopilot !== '';
      case 10: return formData.pain_energy !== '';
      case 11: return formData.pain_gestures !== '';
      case 12: return formData.pain_connection !== '';
      case 13: return formData.age_cluster !== '';
      case 14: return true;
      case 15: return formData.referral_source !== '';
      case 16: return formData.improvement_areas.length >= 1;
      case 17: return formData.work_schedule !== '';
      case 18: return formData.time_budget_minutes !== 0;
      case 19: return outcomeRevealed;
      case 20: return formData.accepted_terms && formData.accepted_privacy;
      case 21: return personalizationDone;
      case 22: return true;
      case 23: return true;
      case 24: return true;
      default: return true;
    }
  };

  /* ── Shared payload builder ──────────────────────────────────── */
  const buildOnboardingPayload = () => {
    const improvementToTension = {
      quality_time: 'time_together', home_management: 'home_organization',
      emotional_presence: 'communication', reduce_tension: 'work_stress', confidence: 'intimacy',
    };
    const kidsMap = () => {
      if (formData.has_kids === 'no') return 'none';
      if (formData.kids_ages.length === 0) return 'none';
      if (formData.kids_ages.length > 1) return 'mixed';
      const age = formData.kids_ages[0];
      if (age === 'toddlers' || age === 'young') return 'young';
      return 'grown';
    };
    return {
      language: formData.language,
      kids_cluster: kidsMap(),
      focus: 'both',
      time_budget_minutes: formData.time_budget_minutes > 0 ? Math.max(formData.time_budget_minutes, 5) : 10,
      tension_points: formData.improvement_areas.map(a => improvementToTension[a] || a),
      reminder_enabled: false,
      timezone: deviceTimezone() || undefined,
      partner_name: '',
      motivation: formData.motivation,
      relationship_type: formData.relationship_status,
      age_cluster: formData.age_cluster,
      referral_source: formData.referral_source,
      work_schedule: formData.work_schedule,
      improvement_areas: formData.improvement_areas,
      pain_autopilot: formData.pain_autopilot,
      pain_energy: formData.pain_energy,
      pain_gestures: formData.pain_gestures,
      pain_connection: formData.pain_connection,
    };
  };

  // True when the payload has at least one user-supplied answer (not just
  // schema defaults). Mirrors the backend guard in submit_onboarding_v2 so we
  // never POST a "ghost onboarding" that would otherwise be silently dropped.
  const payloadHasRealAnswers = (payload) => {
    if (!payload) return false;
    const scalars = ['relationship_type','partner_name','age_cluster','work_schedule','referral_source','pain_autopilot','pain_energy','pain_gestures','pain_connection'];
    if (scalars.some(k => payload[k] && payload[k] !== '' && payload[k] !== 'none')) return true;
    const lists = ['motivation','improvement_areas','tension_points'];
    if (lists.some(k => Array.isArray(payload[k]) && payload[k].length > 0)) return true;
    return false;
  };

  const persistOnboarding = async ({ resetOnEmpty = true } = {}) => {
    if (onboardingSaved) return;
    const payload = buildOnboardingPayload();
    // Guard: never submit an empty/default payload. This happens when a user
    // is dropped directly at the paywall (initialStep=22 via ?step=paywall)
    // without ever filling the form. Silently route them back to the start of
    // the questionnaire instead of polluting the DB with a ghost record.
    if (!payloadHasRealAnswers(payload)) {
      console.warn('[Onboarding] Skipping save — formData has no real answers.');
      try {
        localStorage.removeItem('bh_onboarding_data');
      } catch (_) {}
      // Reset to step 0 ONLY when the caller asked for it. During a paywall
      // checkout press we must NOT bounce the user out of the paywall while
      // StoreKit / Google Play / Stripe is being invoked — it makes the user
      // think the app crashed and the payment sheet was dismissed.
      if (resetOnEmpty) {
        setCurrentStep(0);
      }
      // Throw so callers (checkout flows) abort the post-save action.
      const err = new Error('onboarding_incomplete');
      err.code = 'onboarding_incomplete';
      throw err;
    }
    let freshHeaders = { ...(authCfg.headers || {}), 'Content-Type': 'application/json' };
    try {
      const { data: freshSession } = await supabase.auth.getSession();
      if (freshSession?.session?.access_token) {
        freshHeaders = { Authorization: `Bearer ${freshSession.session.access_token}`, 'Content-Type': 'application/json' };
      }
    } catch (_) { /* use existing headers */ }
    await axios.post(`${API}/profile/onboarding-v2`, payload, {
      headers: freshHeaders,
      withCredentials: authCfg.withCredentials,
    });
    updateUser({ ...payload, onboarding_completed: true, onboarding_completed_at: new Date().toISOString() });
    setOnboardingSaved(true);
    // Clean up local onboarding data now that it's saved to the server
    try {
      localStorage.removeItem('bh_onboarding_data');
      localStorage.removeItem('bh_onboarding_form');
    } catch (_) {}
  };

  // Check if user is authenticated (has a Supabase session or userId)
  const isAuthenticated = !!(user?.user_id || user?.id || authCfg.accessToken);

  /* ── Navigation ───────────────────────────────────────────────── */
  const handleNext = async () => {
    // GDPR: accepting the Privacy Policy at step 20 also grants consent
    // for product analytics (Firebase Analytics / GA4). The policy text
    // explicitly discloses this. Without this call analytics stays OFF.
    if (currentStep === 20 && formData.accepted_privacy) {
      try { await setAnalyticsConsent(true); } catch (_) { /* non-fatal */ }
    }

    // At step 20: only persist if user is already authenticated (returning users)
    if (currentStep === 20 && !onboardingSaved && isAuthenticated) {
      try {
        setSaving(true);
        await persistOnboarding();
      } catch (err) {
        console.warn('Onboarding early save failed (will retry at checkout):', err);
      } finally {
        setSaving(false);
      }
    }

    // After step 21 (personalizing done): if not authenticated, redirect to login
    if (currentStep === 21 && !isAuthenticated) {
      // Save the onboarding payload to localStorage for the login flow to pick up.
      // Only persist if the form actually has real answers — otherwise the
      // post-OAuth replay would create a ghost-onboarded user.
      try {
        const payload = buildOnboardingPayload();
        if (payloadHasRealAnswers(payload)) {
          localStorage.setItem('bh_onboarding_data', JSON.stringify(payload));
        } else {
          localStorage.removeItem('bh_onboarding_data');
          console.warn('[Onboarding] Step 21 reached with empty form — restarting questionnaire instead of redirecting to login.');
          setCurrentStep(0);
          return;
        }
      } catch (_) {}
      navigate('/login?from=onboarding', { replace: true });
      return;
    }

    // After step 21 (personalizing done): if authenticated, check beta tester status
    if (currentStep === 21 && isAuthenticated) {
      try {
        // Ensure onboarding is persisted
        if (!onboardingSaved) {
          setSaving(true);
          await persistOnboarding();
          setSaving(false);
        }
        // Check if user is a beta tester — skip paywall entirely
        let freshHeaders = { ...(authCfg.headers || {}), 'Content-Type': 'application/json' };
        try {
          const { data: freshSession } = await supabase.auth.getSession();
          if (freshSession?.session?.access_token) {
            freshHeaders = { Authorization: `Bearer ${freshSession.session.access_token}`, 'Content-Type': 'application/json' };
          }
        } catch (_) {}
        const betaRes = await axios.get(`${API}/auth/check-beta`, { headers: freshHeaders });
        if (betaRes.data?.is_beta) {
          await fetchUserData();
          navigate('/dashboard', { replace: true });
          return;
        }
      } catch (err) {
        console.warn('Beta check failed, proceeding to paywall:', err);
      }
    }

    if (currentStep < totalSteps - 1) setCurrentStep(s => s + 1);
  };

  const handleBack = () => {
    // On paywall steps, never go back past step 22
    if (currentStep >= 22) {
      setCurrentStep(22);
      return;
    }
    if (currentStep > 0) {
      setCurrentStep(s => s - 1);
    }
  };

  const updateField = async (field, value) => {
    if (field === 'language') {
      try {
        await i18n.changeLanguage(value);
        localStorage.setItem('betterhusbandUserLang', value);
        setFormData(prev => ({ ...prev, language: value }));
      } catch (err) { console.warn('Failed to change language', err); }
      return;
    }
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleImprovement = (area) => {
    setFormData(prev => {
      const current = prev.improvement_areas;
      if (current.includes(area)) return { ...prev, improvement_areas: current.filter(a => a !== area) };
      if (current.length < 3) return { ...prev, improvement_areas: [...current, area] };
      return prev;
    });
  };

  const toggleKidsAge = (age) => {
    setFormData(prev => {
      const current = prev.kids_ages;
      if (current.includes(age)) return { ...prev, kids_ages: current.filter(a => a !== age) };
      return { ...prev, kids_ages: [...current, age] };
    });
  };

  /* ── Submit (legacy path — direct to dashboard without paywall) ── */
  const handleSubmit = async () => {
    if (saving || authCfg.loading) return;
    setSaving(true);
    try {
      await persistOnboarding();
      onComplete?.();
      setShowTransition(true);
      setTimeout(() => navigate('/dashboard', { replace: true }), 2500);
    } catch (error) {
      console.error('Onboarding error:', error);
      if (error?.response?.status === 401) {
        try {
          const { data: refreshData } = await supabase.auth.refreshSession();
          if (refreshData?.session?.access_token) {
            setOnboardingSaved(false); // reset to retry with new token
            await persistOnboarding();
            onComplete?.();
            setShowTransition(true);
            setTimeout(() => navigate('/dashboard', { replace: true }), 2500);
            return;
          }
        } catch (_retryErr) {
          console.error('Onboarding retry after 401 also failed:', _retryErr);
        }
        toast.error(lang === 'it' ? 'Sessione scaduta. Effettua nuovamente il login.' : lang === 'es' ? 'Sesión expirada. Por favor inicia sesión de nuevo.' : 'Session expired. Please log in again.');
        navigate('/login', { replace: true });
        return;
      }
      toast.error(error?.response?.data?.detail || error?.message || 'Error saving');
    } finally { setSaving(false); }
  };

  const handleStripeCheckout = async () => {
    if (checkoutLoading || authCfg.loading) return;
    if (!selectedPlan) {
      toast.error(lang === 'it' ? 'Seleziona un piano.' : lang === 'es' ? 'Selecciona un plan.' : 'Please select a plan.');
      return;
    }
    setCheckoutLoading(true);
    try {
      console.log('[Stripe] Checkout started');
      let uid = user?.user_id || user?.id;
      console.log('[Stripe] User ID from context:', uid);
      
      if (!uid) {
        // Fallback 1: get user ID from localStorage (email/password registration)
        console.log('[Stripe] Fallback 1: checking localStorage for email/password user');
        const emailPasswordUserId = localStorage.getItem('betterhusbandUserId');
        if (emailPasswordUserId) {
          uid = emailPasswordUserId;
          console.log('[Stripe] Fallback 1 SUCCESS: Found user_id in localStorage:', uid);
        } else {
          console.log('[Stripe] Fallback 1 failed: No user_id in localStorage');
        }
      }
      if (!uid) {
        // Fallback 2: get user ID from cached Supabase session (OAuth)
        console.log('[Stripe] Fallback 2: getting cached Supabase session');
        const { data: sessionData } = await supabase.auth.getSession();
        uid = sessionData?.session?.user?.id;
        console.log('[Stripe] Fallback 2 result:', uid, sessionData?.session ? 'has session' : 'no session');
      }
      if (!uid) {
        // Fallback 3: force-refresh the Supabase session (token may have expired)
        try {
          console.log('[Stripe] Fallback 3: refreshing Supabase session');
          const { data: refreshData } = await supabase.auth.refreshSession();
          uid = refreshData?.session?.user?.id;
          console.log('[Stripe] Fallback 3 result:', uid);
        } catch (err) { 
          console.error('[Stripe] Fallback 3 error:', err);
        }
      }
      if (!uid && authCfg.accessToken) {
        // Fallback 4: decode user ID from JWT access token
        try {
          console.log('[Stripe] Fallback 4: decoding JWT token');
          const payload = JSON.parse(atob(authCfg.accessToken.split('.')[1]));
          uid = payload.sub;
          console.log('[Stripe] Fallback 4 result:', uid);
        } catch (err) { 
          console.error('[Stripe] Fallback 4 error:', err);
        }
      }
      if (!uid) {
        console.error('[Stripe] Failed to get user ID from all fallbacks');
        toast.error(lang === 'it' ? 'Sessione scaduta. Effettua nuovamente il login.' : lang === 'es' ? 'Sesión expirada. Por favor inicia sesión de nuevo.' : 'Session expired. Please log in again.');
        navigate('/login', { replace: true });
        return;
      }
      console.log('[Stripe] Final user ID:', uid);

      // Save onboarding data if not already saved (step 16 saves it early).
      // Pass resetOnEmpty:false so an empty payload does NOT bounce the user
      // back to step 0 while the Stripe Checkout page is opening — the user
      // must stay on the paywall.
      if (!onboardingSaved) {
        console.log('[Stripe] Saving onboarding data...');
        try {
          await persistOnboarding({ resetOnEmpty: false });
          console.log('[Stripe] Onboarding saved successfully');
        } catch (err) {
          console.warn('[Stripe] Onboarding save failed (continuing to checkout):', err?.response?.data || err?.message || err);
          // Keep onboardingSaved false to retry when possible, but do not block checkout.
        }
      } else {
        console.log('[Stripe] Onboarding already saved, skipping');
      }

      // Ensure Supabase session is fresh before redirecting to Stripe
      console.log('[Stripe] Refreshing Supabase session before checkout');
      const { data: freshSession } = await supabase.auth.refreshSession();
      console.log('[Stripe] Session refresh:', freshSession?.session ? 'success' : 'failed');
      
      if (freshSession?.session?.access_token) {
        // Store in sessionStorage for retrieval after Stripe redirect
        console.log('[Stripe] Storing token in sessionStorage');
        sessionStorage.setItem('sb-auth-token', freshSession.session.access_token);
        console.log('[Stripe] Token stored, verifying storage');
        const stored = sessionStorage.getItem('sb-auth-token');
        console.log('[Stripe] Verification:', stored ? 'token found in sessionStorage' : 'ERROR: token not found');
      }

      // Create Stripe checkout session
      console.log('[Stripe] Creating checkout session', {
        user_id: uid,
        plan_type: selectedPlan,
        language: lang,
        origin: window.location.origin
      });
      const response = await axios.post(`${API}/subscription/create-checkout`, {
        user_id: uid,
        origin_url: window.location.origin,
        language: lang === 'it' ? 'it' : lang === 'es' ? 'es' : 'en',
        plan_type: selectedPlan,
      });
      console.log('[Stripe] Checkout response:', {
        has_url: !!response?.data?.checkout_url,
        has_session_id: !!response?.data?.session_id,
        status: response?.status
      });
      
      const url = response?.data?.checkout_url;
      if (url) {
        console.log('[Stripe] Got checkout URL, storing token and waiting 100ms before redirect');
        // Small delay to ensure session is stored before redirect
        await new Promise(r => setTimeout(r, 100));
        console.log('[Stripe] Redirecting to Stripe:', url.substring(0, 60) + '...');
        window.location.href = url;
        return;
      }
      throw new Error('No checkout URL returned');
    } catch (error) {
      console.error('[Stripe] Checkout error:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        stack: error.stack
      });
      toast.error(lang === 'it' ? 'Errore durante il checkout. Riprova.' : lang === 'es' ? 'Error durante el pago. Inténtalo de nuevo.' : 'Checkout error. Please try again.');
      // If payment failed but onboarding is already completed, return to paywall page.
      if (currentStep >= 22) {
        setCurrentStep(22);
      }
    } finally { 
      console.log('[Stripe] Checkout attempt completed');
      setCheckoutLoading(false); 
    }
  };

  // Native IAP checkout (iOS / Android)
  // Resolve the current user ID via the same fallback chain used for Stripe.
  // Required because cordova-plugin-purchase's `approved` handler verifies the
  // receipt against the backend and refuses to do so without a userId — which
  // would surface as a generic error toast right after StoreKit charges the
  // reviewer (Apple Guideline 2.1(b) rejection on iPad sandbox).
  const resolveUserIdForCheckout = async () => {
    let uid = user?.user_id || user?.id;
    if (!uid) uid = localStorage.getItem('betterhusbandUserId') || null;
    if (!uid) {
      try {
        const { data } = await supabase.auth.getSession();
        uid = data?.session?.user?.id || null;
      } catch (_e) { /* ignore */ }
    }
    if (!uid) {
      try {
        const { data } = await supabase.auth.refreshSession();
        uid = data?.session?.user?.id || null;
      } catch (_e) { /* ignore */ }
    }
    if (!uid && authCfg.accessToken) {
      try {
        const payload = JSON.parse(atob(authCfg.accessToken.split('.')[1]));
        uid = payload?.sub || null;
      } catch (_e) { /* ignore */ }
    }
    return uid;
  };

  // Restore Purchases — required by Apple for a non-consumable, and the only
  // way someone who already bought lifetime gets back in on a new device.
  // Refreshing the user afterwards matters: the restore re-fires `approved`,
  // verify-iap grants premium server-side, but nothing here would notice
  // until the next launch without an explicit refetch.
  const handleRestorePurchases = async () => {
    toast.info(lang === 'it' ? 'Ripristino acquisti…' : lang === 'es' ? 'Restaurando compras…' : 'Restoring purchases…');
    try {
      await iapRestore();
      // Give verify-iap a moment to land before we read the profile back.
      await new Promise((resolve) => setTimeout(resolve, 1500));
      await fetchUserData();
      toast.success(lang === 'it' ? 'Acquisti ripristinati' : lang === 'es' ? 'Compras restauradas' : 'Purchases restored');
    } catch (restoreErr) {
      console.error('[IAP] Restore failed:', restoreErr);
      toast.error(lang === 'it' ? 'Ripristino non riuscito' : lang === 'es' ? 'No se pudo restaurar' : "Couldn't restore purchases");
    }
  };

  const handleIAPCheckout = async () => {
    if (iapPurchasing || checkoutLoading) return;
    if (!selectedPlan) {
      toast.error(lang === 'it' ? 'Seleziona un piano.' : lang === 'es' ? 'Selecciona un plan.' : 'Please select a plan.');
      return;
    }

    // Guard: if products haven't loaded yet, try refreshing once
    let checkoutProducts = iapProducts;
    if (iapProducts.length === 0) {
      console.warn('[IAP] No products loaded — attempting refresh before purchase');
      toast.info(lang === 'it' ? 'Caricamento piani in corso…' : lang === 'es' ? 'Cargando planes…' : 'Loading plans…');
      let refreshedProducts = [];
      try { refreshedProducts = await iapRefresh(); } catch (refreshErr) { /* ignore */ }
      checkoutProducts = Array.isArray(refreshedProducts) ? refreshedProducts : [];
      // Re-check after refresh
      if (checkoutProducts.length === 0) {
        toast.error(lang === 'it'
          ? 'Impossibile contattare l\u2019App Store. Controlla la connessione e riprova.'
          : lang === 'es'
          ? 'No se pudo conectar con la App Store. Comprueba tu conexión e inténtalo de nuevo.'
          : 'Couldn\u2019t reach the App Store. Please check your connection and try again.');
        return;
      }
    }

    setCheckoutLoading(true);
    try {
      const productId = PRODUCT_IDS.LIFETIME;
      console.log('[IAP] Starting purchase for product:', productId, 'loaded products:', checkoutProducts.map(product => product.id));

      // Save onboarding data before purchase. resetOnEmpty:false keeps the
      // user on the paywall (don't snap them back to step 0) while the Google
      // Play / App Store sheet is being invoked.
      if (!onboardingSaved) {
        try { await persistOnboarding({ resetOnEmpty: false }); } catch (err) {
          console.warn('[IAP] Onboarding save failed (continuing):', err?.message);
        }
      }

      // CRITICAL: ensure we have a backend user_id BEFORE invoking StoreKit so
      // the approved-receipt handler can verify the purchase. Without this the
      // reviewer is charged but sees a generic error toast.
      const uid = await resolveUserIdForCheckout();
      if (!uid) {
        console.error('[IAP] No user ID available — aborting purchase before StoreKit');
        toast.error(lang === 'it'
          ? 'Sessione scaduta. Effettua nuovamente l\u2019accesso e riprova.'
          : lang === 'es'
          ? 'Tu sesión ha expirado. Inicia sesión de nuevo e inténtalo otra vez.'
          : 'Your session has expired. Please sign in again and retry.');
        navigate('/login', { replace: true });
        return;
      }

      const result = await iapPurchase(productId, { userId: uid });
      if (result !== false) {
        console.log('[IAP] Purchase initiated — handing off to PremiumSuccess for DB-confirmed activation');
        // Show the "configuring your experience" overlay so the user never
        // sees the paywall flash back during the redirect.
        setShowTransition(true);
        // Hand off to the centralized success page. It will poll
        // /subscription/status until the DB row confirms trial|premium and
        // only then enable the Continue button — eliminating the
        // read-after-write race that previously bounced users back to the
        // paywall. After Continue, the user lands on /dashboard.
        navigate('/premium/success?source=iap&return=/dashboard', { replace: true });
        return;
      } else {
        // result === false typically means the user cancelled the StoreKit
        // sheet or no offer was returned. Don't show a scary error — that
        // mimics the rejection screenshot.
        console.warn('[IAP] Purchase returned false (cancelled or no offer)');
      }
    } catch (err) {
      console.error('[IAP] Purchase error:', err);
      const msg = String(err?.message || '').toLowerCase();
      const isCancel = err?.code === 6 || msg.includes('cancel');
      if (isCancel) {
        // Silent — user explicitly dismissed the StoreKit sheet.
        return;
      }
      // Verification timeouts / transient backend errors: the receipt is
      // already queued in localStorage by the IAP hook and will be replayed
      // on next launch. Surface a recoverable message instead of "error".
      if (msg.includes('verif') || msg.includes('timeout') || msg.includes('network')) {
        toast.info(lang === 'it'
          ? 'Stiamo finalizzando il tuo acquisto. Se il pagamento è andato a buon fine verrà attivato a breve.'
          : lang === 'es'
          ? 'Estamos finalizando tu compra. Si el pago se realizó correctamente, se activará en breve.'
          : 'We\u2019re finalizing your purchase. If payment went through, your subscription will activate shortly.');
        return;
      }
      toast.error(lang === 'it'
        ? 'Acquisto non completato. Riprova tra qualche istante.'
        : lang === 'es'
        ? 'No se completó la compra. Inténtalo de nuevo en un momento.'
        : 'Purchase didn\u2019t complete. Please try again in a moment.');
    } finally {
      setCheckoutLoading(false);
    }
  };

  /* ── Step renderer ────────────────────────────────────────────── */
  const renderStep = () => {
    switch (currentStep) {

      /* ── 0: Language ── */
      case 0:
        return (
          <div className="flex-1 flex flex-col">
            <h1 className="text-2xl font-bold text-[#1A211E] text-center">
              {lang === 'it' ? 'Lingua preferita' : lang === 'es' ? 'Idioma preferido' : 'Preferred language'}
            </h1>
            <div className="flex-1 flex flex-col justify-center">
            <div className="space-y-3">
              <OptionBtn selected={formData.language === 'it'} onClick={() => updateField('language', 'it')}>
                🇮🇹 Italiano
              </OptionBtn>
              <OptionBtn selected={formData.language === 'en'} onClick={() => updateField('language', 'en')}>
                🇬🇧 English
              </OptionBtn>
              <OptionBtn selected={formData.language === 'es'} onClick={() => updateField('language', 'es')}>
                🇪🇸 Español
              </OptionBtn>
            </div>
            </div>
          </div>
        );

      /* ── 1: Landing – The questions that don't go away ── */
      case 1:
        return (
          <div className="flex-1 flex flex-col items-center justify-center px-8"
               style={{ paddingTop: 40, paddingBottom: 40 }}>
            <motion.h1
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: 'easeOut' }}
              style={{
                fontFamily: 'Manrope-Variable, Manrope, sans-serif',
                fontSize: 22, fontWeight: 700, color: '#1A211E',
                textAlign: 'center', marginBottom: 36, lineHeight: 1.3,
              }}
            >
              {lang === 'it' ? 'Le domande che non se ne vanno.' : lang === 'es' ? 'Las preguntas que no se van.' : 'The questions that don\u2019t go away.'}
            </motion.h1>
            {[
              { it: 'Faccio abbastanza?', en: 'Am I doing enough?', es: '¿Estoy haciendo suficiente?' },
              { it: 'Sono abbastanza presente?', en: 'Am I present enough?', es: '¿Estoy lo bastante presente?' },
              { it: 'Gli sto dando il meglio di me\u2026 o quello che resta?', en: 'Am I giving them the best of me\u2026 or what\u2019s left of me?', es: '¿Le estoy dando lo mejor de mí\u2026 o lo que queda?' },
              { it: 'Sono il partner di cui ha bisogno?', en: 'Am I the partner she needs?', es: '¿Soy la pareja que ella necesita?' },
              { it: 'Sto diventando il padre che meritano?', en: 'Am I becoming the father they deserve?', es: '¿Me estoy convirtiendo en el padre que merecen?' },
            ].map((q, i) => (
              <motion.p
                key={i}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 + i * 0.35, duration: 0.6, ease: 'easeOut' }}
                style={{
                  fontFamily: 'Manrope-Variable, Manrope, sans-serif',
                  fontSize: 17, fontWeight: 400, fontStyle: 'italic',
                  color: 'rgba(26,33,30,0.82)', textAlign: 'center',
                  lineHeight: 1.6, marginBottom: 14,
                }}
              >
                {lang === 'it' ? q.it : lang === 'es' ? q.es : q.en}
              </motion.p>
            ))}
          </div>
        );

      /* ── 2: Landing – You carry these questions quietly ── */
      case 2:
        return (
          <div className="flex-1 flex flex-col items-center justify-center px-8"
               style={{ paddingTop: 40, paddingBottom: 40 }}>
            <motion.h1
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: 'easeOut' }}
              style={{
                fontFamily: 'Manrope-Variable, Manrope, sans-serif',
                fontSize: 22, fontWeight: 700, color: '#1A211E',
                textAlign: 'center', marginBottom: 36, lineHeight: 1.3,
              }}
            >
              {lang === 'it' ? 'Ti poni queste domande in silenzio.' : lang === 'es' ? 'Te haces estas preguntas en silencio.' : 'You carry these questions quietly.'}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.6, ease: 'easeOut' }}
              style={{
                fontFamily: 'Manrope-Variable, Manrope, sans-serif',
                fontSize: 16, fontWeight: 400,
                color: 'rgba(26,33,30,0.82)', textAlign: 'center',
                lineHeight: 1.7, marginBottom: 20, maxWidth: 320,
              }}
            >
              {lang === 'it'
                ? 'Tra lavoro, responsabilit\u00e0 e tutto quello che c\u2019\u00e8 in mezzo\u2026 \u00e8 facile sentirsi sempre un passo indietro.'
                : lang === 'es'
                ? 'Entre el trabajo, las responsabilidades y todo lo que hay en medio\u2026 es fácil sentir que siempre vas un paso atrás.'
                : 'Between work, responsibilities, and everything in between\u2026 it\u2019s easy to feel like you\u2019re always a step behind.'}
            </motion.p>
          </div>
        );

      /* ── 3: Landing – The fact that you're asking… matters ── */
      case 3:
        return (
          <div className="flex-1 flex flex-col items-center justify-center px-8"
               style={{ paddingTop: 40, paddingBottom: 40 }}>
            <motion.h1
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: 'easeOut' }}
              style={{
                fontFamily: 'Manrope-Variable, Manrope, sans-serif',
                fontSize: 22, fontWeight: 700, color: '#1A211E',
                textAlign: 'center', marginBottom: 36, lineHeight: 1.3,
              }}
            >
              {lang === 'it' ? 'Ma il fatto che te le stai facendo\u2026 conta.' : lang === 'es' ? 'Pero el hecho de que te las estés haciendo\u2026 importa.' : 'But the fact that you\u2019re asking them\u2026 matters.'}
            </motion.h1>
            {[
              { it: 'Significa che ci tieni.', en: 'It means you care.', es: 'Significa que te importa.' },
              { it: 'Significa che ci stai provando.', en: 'It means you\u2019re trying.', es: 'Significa que lo estás intentando.' },
              { it: 'Significa che non hai mollato.', en: 'It means you haven\u2019t checked out.', es: 'Significa que no te has rendido.' },
            ].map((line, i) => (
              <motion.p
                key={i}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 + i * 0.5, duration: 0.6, ease: 'easeOut' }}
                style={{
                  fontFamily: 'Manrope-Variable, Manrope, sans-serif',
                  fontSize: 16, fontWeight: 400,
                  color: 'rgba(26,33,30,0.82)', textAlign: 'center',
                  lineHeight: 1.7, marginBottom: 16, maxWidth: 320,
                }}
              >
                {lang === 'it' ? line.it : lang === 'es' ? line.es : line.en}
              </motion.p>
            ))}
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 2.2, duration: 0.7, ease: 'easeOut' }}
              style={{
                fontFamily: 'Manrope-Variable, Manrope, sans-serif',
                fontSize: 17, fontWeight: 600,
                color: '#1A211E', textAlign: 'center',
                lineHeight: 1.6, marginTop: 8,
              }}
            >
              {lang === 'it' ? 'E questo dice gi\u00e0 molto sull\u2019uomo che sei.' : lang === 'es' ? 'Y eso ya dice mucho del hombre que eres.' : 'And that already says a lot about the man you are.'}
            </motion.p>
          </div>
        );

      /* ── 4: Landing – You don't need to be perfect ── */
      case 4:
        return (
          <div className="flex-1 flex flex-col items-center justify-center px-8"
               style={{ paddingTop: 40, paddingBottom: 40 }}>
            <motion.h1
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: 'easeOut' }}
              style={{
                fontFamily: 'Manrope-Variable, Manrope, sans-serif',
                fontSize: 22, fontWeight: 700, color: '#1A211E',
                textAlign: 'center', marginBottom: 36, lineHeight: 1.3,
              }}
            >
              {lang === 'it' ? 'Non devi essere perfetto.' : lang === 'es' ? 'No tienes que ser perfecto.' : 'You don\u2019t need to be perfect.'}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.6, ease: 'easeOut' }}
              style={{
                fontFamily: 'Manrope-Variable, Manrope, sans-serif',
                fontSize: 16, fontWeight: 400,
                color: 'rgba(26,33,30,0.82)', textAlign: 'center',
                lineHeight: 1.7, marginBottom: 20, maxWidth: 320,
              }}
            >
              {lang === 'it'
                ? 'Non servono grandi gesti o grandi cambiamenti.'
                : lang === 'es'
                ? 'No hacen falta grandes gestos ni grandes cambios.'
                : 'You don\u2019t need grand gestures or big changes.'}
            </motion.p>
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.2, duration: 0.6, ease: 'easeOut' }}
              style={{
                fontFamily: 'Manrope-Variable, Manrope, sans-serif',
                fontSize: 16, fontWeight: 400,
                color: 'rgba(26,33,30,0.75)', textAlign: 'center',
                lineHeight: 1.7, marginBottom: 20, maxWidth: 320,
              }}
            >
              {lang === 'it'
                ? 'Ti serve un modo per mostrarti presente\u2026\nun po\u2019 di pi\u00f9, un po\u2019 meglio, ogni giorno.'
                : lang === 'es'
                ? 'Solo necesitas una forma de estar presente\u2026\nun poco más, un poco mejor, cada día.'
                : 'You just need a way to show up\u2026\na little more, a little better, every day.'}
            </motion.p>
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 2.0, duration: 0.7, ease: 'easeOut' }}
              style={{
                fontFamily: 'Manrope-Variable, Manrope, sans-serif',
                fontSize: 17, fontWeight: 600,
                color: '#1A211E', textAlign: 'center',
                lineHeight: 1.6, marginTop: 8,
              }}
            >
              {lang === 'it' ? 'E devi poter vedere la crescita.' : lang === 'es' ? 'Y necesitas poder ver el crecimiento.' : 'And you need to see the growth.'}
            </motion.p>
          </div>
        );

      /* ── 5: Welcome / Dashboard preview ── */
      case 5:
        return (
          <div className="flex-1 flex flex-col" style={{ gap: 0 }}>

            {/* Isometric garden — fullView with gradient, matching Dashboard GardenSubHeader */}
            <div style={{
              backgroundColor: '#F4EFE8',
              position: 'relative',
              overflow: 'hidden',
            }}>
              <IsometricGarden
                grid={makeWelcomeGrid(welcomeTree)}
                completionAnimation={welcomeAnim}
                fullView
              />
            </div>

            {/* Dashboard-style content: card + hold button */}
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <div style={{ width: '100%', maxWidth: 390, position: 'relative' }}>
                <div style={{ padding: '12px 24px 0' }}>

                  {/* White suggestion card — exact Dashboard styling */}
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    style={{
                      background: '#FFFFFF',
                      borderRadius: 16,
                      padding: '16px 18px',
                      boxShadow: '0 8px 20px rgba(0, 0, 0, 0.12)',
                      border: '1px solid rgba(126, 168, 108, 0.24)',
                    }}
                  >
                    <div style={{
                      fontFamily: 'Manrope-Variable',
                      fontSize: 14, fontWeight: 500, lineHeight: 1.4,
                      color: '#000000',
                    }}>
                      {lang === 'it'
                        ? 'Completa 1 gesto significativo al giorno. Fai crescere la tua presenza. Guarda la crescita.'
                        : lang === 'es'
                        ? 'Completa 1 gesto significativo al día. Haz crecer tu presencia. Observa el crecimiento.'
                        : 'Complete 1 meaningful gesture a day. Grow your presence. See the growth.'}
                    </div>
                  </motion.div>

                  {/* Hold-to-complete circle — disappears after completion */}
                  {!welcomeDone && (
                    <HoldToCompleteButton
                      onComplete={() => {
                        if (welcomeDone) return;
                        setWelcomeDone(true);
                        setWelcomeAnim({ tile: [7, 7], start: performance.now() });
                        const tree = MATURE_TREES[Math.floor(Math.random() * MATURE_TREES.length)];
                        setTimeout(() => {
                          setWelcomeTree(tree);
                          const g = createInitialGarden();
                          g[7][7] = tree;
                          try { localStorage.setItem('bh_garden_grid', JSON.stringify(g)); } catch (_) {}
                        }, 600);
                        setTimeout(() => setShowContinue(true), 2000);
                      }}
                      label={lang === 'it' ? 'Lo farò' : lang === 'es' ? 'Lo haré' : 'I\'ll do it'}
                    />
                  )}

                  {/* Cheerful phrase after planting */}
                  {welcomeDone && (
                    <motion.p
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.8 }}
                      style={{
                        textAlign: 'center',
                        marginTop: 16,
                        fontFamily: 'Manrope-Variable, Manrope, sans-serif',
                        fontSize: 15,
                        fontWeight: 600,
                        color: '#1A211E',
                      }}
                    >
                      {lang === 'it'
                        ? 'Semplice. Ma efficace! Sei sulla strada giusta! \ud83c\udf33'
                        : lang === 'es'
                        ? '¡Simple, pero efectivo! ¡Vas por el buen camino! \ud83c\udf33'
                        : 'Simple. But effective! You are on the right track! \ud83c\udf33'}
                    </motion.p>
                  )}

                </div>
              </div>
            </div>

            {/* Spacer */}
            <div style={{ flex: 1 }} />

            {/* Ok button at the bottom */}
            {showContinue && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                style={{ padding: '0 24px 24px' }}
              >
                <button
                  onClick={handleNext}
                  style={{
                    width: '100%', height: 52,
                    background: 'linear-gradient(to right, #E36A55, #D35C48)', color: '#ffffff', border: 'none', cursor: 'pointer',
                    fontFamily: 'Manrope-Variable, Manrope, sans-serif',
                    fontWeight: 600, fontSize: 15, borderRadius: 12,
                    boxShadow: '0 0 20px rgba(227, 106, 85, 0.25)',
                  }}
                >
                  {lang === 'it' ? 'Continua' : lang === 'es' ? 'Continuar' : 'Continue'}
                </button>
              </motion.div>
            )}
          </div>
        );

      /* ── 6: Motivation ── */
      case 6: {
        const options = [
          { value: 'improve_relationship', label: lang === 'it' ? 'Voglio migliorare la mia relazione' : lang === 'es' ? 'Quiero mejorar mi relación' : 'I want to improve my relationship' },
          { value: 'be_present', label: lang === 'it' ? 'Voglio essere più presente' : lang === 'es' ? 'Quiero estar más presente' : 'I want to be more present' },
          { value: 'do_more', label: lang === 'it' ? 'Sento che potrei fare di più in casa' : lang === 'es' ? 'Siento que podría hacer más en casa' : 'I feel like I could do more at home' },
          { value: 'reconnect', label: lang === 'it' ? 'Voglio riconnettermi con la mia partner' : lang === 'es' ? 'Quiero reconectar con mi pareja' : 'I want to reconnect with my partner' },
        ];
        return (
          <div className="flex-1 flex flex-col">
            <h1 className="text-2xl font-bold text-[#1A211E] text-center">
              {lang === 'it' ? 'Perché hai scaricato Better Husband?' : lang === 'es' ? '¿Por qué descargaste Better Husband?' : 'Why did you download Better Husband?'}
            </h1>
            <p className="text-sm text-stone-900 mt-2 text-center">
              {lang === 'it' ? 'Questo ci aiuta a personalizzare la tua esperienza.' : lang === 'es' ? 'Esto nos ayuda a personalizar tu experiencia.' : 'This helps us personalize your experience.'}
            </p>
            <div className="flex-1 flex flex-col justify-center">
            <div className="space-y-3">
              {options.map(o => {
                const sel = formData.motivation.includes(o.value);
                return (
                  <OptionBtn key={o.value} selected={sel} onClick={() => {
                    setFormData(prev => ({
                      ...prev,
                      motivation: sel
                        ? prev.motivation.filter(v => v !== o.value)
                        : [...prev.motivation, o.value],
                    }));
                  }}>
                    {o.label}
                  </OptionBtn>
                );
              })}
            </div>
            </div>
          </div>
        );
      }

      /* ── 7: Relationship Type ── */
      case 7: {
        const relOptions = [
          { value: 'married', label: lang === 'it' ? 'Sposato' : lang === 'es' ? 'Casado' : 'Married' },
          { value: 'living_together', label: lang === 'it' ? 'Convivente' : lang === 'es' ? 'Convivientes' : 'Living together' },
          { value: 'in_relationship', label: lang === 'it' ? 'In una relazione' : lang === 'es' ? 'En una relación' : 'In a relationship' },
        ];
        return (
          <div className="flex-1 flex flex-col">
            <h1 className="text-2xl font-bold text-[#1A211E] text-center">
              {lang === 'it' ? 'Qual è la tua situazione sentimentale?' : lang === 'es' ? '¿Cuál es tu situación sentimental?' : "What's your relationship status?"}
            </h1>
            <div className="flex-1 flex flex-col justify-center">
            <div className="space-y-3">
              {relOptions.map(o => (
                <OptionBtn key={o.value} selected={formData.relationship_status === o.value} onClick={() => updateField('relationship_status', o.value)}>
                  {o.label}
                </OptionBtn>
              ))}
            </div>
            </div>
          </div>
        );
      }

      /* ── 8: Kids ── */
      case 8: {
        const kidsAgeOptions = [
          { value: 'toddlers', label: lang === 'it' ? 'Piccoli (0–5)' : lang === 'es' ? 'Pequeños (0–5)' : 'Toddlers (0–5)' },
          { value: 'young', label: lang === 'it' ? 'Bambini (6–12)' : lang === 'es' ? 'Niños (6–12)' : 'Young (6–12)' },
          { value: 'teenagers', label: lang === 'it' ? 'Adolescenti (13–17)' : lang === 'es' ? 'Adolescentes (13–17)' : 'Teenagers (13–17)' },
          { value: 'adults', label: lang === 'it' ? 'Adulti (18+)' : lang === 'es' ? 'Adultos (18+)' : 'Adults (18+)' },
        ];
        return (
          <div className="flex-1 flex flex-col">
            <h1 className="text-2xl font-bold text-[#1A211E] text-center">
              {lang === 'it' ? 'Hai figli?' : lang === 'es' ? '¿Tienes hijos?' : 'Do you have kids?'}
            </h1>
            <div className="flex-1 flex flex-col justify-center">
            <div className="space-y-3">
              <OptionBtn selected={formData.has_kids === 'yes'} onClick={() => updateField('has_kids', 'yes')}>
                {lang === 'it' ? 'Sì' : lang === 'es' ? 'Sí' : 'Yes'}
              </OptionBtn>
              <OptionBtn selected={formData.has_kids === 'no'} onClick={() => setFormData(prev => ({ ...prev, has_kids: 'no', kids_ages: [] }))}>
                {lang === 'it' ? 'No' : lang === 'es' ? 'No' : 'No'}
              </OptionBtn>
            </div>
            {formData.has_kids === 'yes' && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                transition={{ duration: 0.3 }}
                className="mt-6"
              >
                <p className="text-sm font-semibold text-stone-900 uppercase tracking-wide mb-3">
                  {lang === 'it' ? 'Che età hanno?' : lang === 'es' ? '¿Qué edad tienen?' : 'How old are they?'}
                </p>
                <div className="space-y-3">
                  {kidsAgeOptions.map(o => (
                    <OptionBtn key={o.value} selected={formData.kids_ages.includes(o.value)} onClick={() => toggleKidsAge(o.value)}>
                      <span className="flex items-center justify-center">
                        <span>{o.label}</span>
                        {formData.kids_ages.includes(o.value) && <span className="text-white">✓</span>}
                      </span>
                    </OptionBtn>
                  ))}
                </div>
              </motion.div>
            )}
            </div>
          </div>
        );
      }

      /* ── 9: Autopilot (Pain Point) ── */
      case 9: {
        const autopilotScale = [
          { value: 'never', label: lang === 'it' ? 'Mai' : lang === 'es' ? 'Nunca' : 'Never' },
          { value: 'rarely', label: lang === 'it' ? 'Raramente' : lang === 'es' ? 'Raramente' : 'Rarely' },
          { value: 'sometimes', label: lang === 'it' ? 'A volte' : lang === 'es' ? 'A veces' : 'Sometimes' },
          { value: 'often', label: lang === 'it' ? 'Spesso' : lang === 'es' ? 'A menudo' : 'Often' },
          { value: 'very_often', label: lang === 'it' ? 'Molto spesso' : lang === 'es' ? 'Muy a menudo' : 'Very often' },
        ];
        return (
          <div className="flex-1 flex flex-col items-center">
            <h1 className="text-2xl font-bold text-[#1A211E] text-center">
              {lang === 'it' ? 'Ti capita di sentirti in modalità autopilota a casa?' : lang === 'es' ? '¿A veces sientes que estás en modo automático en casa?' : 'Do you sometimes feel like you\'re on autopilot at home?'}
            </h1>
            <div className="flex-1 flex flex-col justify-center w-full">
              <div className="space-y-3 w-full">
                {autopilotScale.map(o => (
                  <OptionBtn key={o.value} selected={formData.pain_autopilot === o.value} onClick={() => updateField('pain_autopilot', o.value)}>
                    {o.label}
                  </OptionBtn>
                ))}
              </div>
            </div>
          </div>
        );
      }

      /* ── 6: Energy (Pain Point) ── */
      case 10: {
        const energyScale = [
          { value: 'never', label: lang === 'it' ? 'Mai' : lang === 'es' ? 'Nunca' : 'Never' },
          { value: 'rarely', label: lang === 'it' ? 'Raramente' : lang === 'es' ? 'Raramente' : 'Rarely' },
          { value: 'sometimes', label: lang === 'it' ? 'A volte' : lang === 'es' ? 'A veces' : 'Sometimes' },
          { value: 'often', label: lang === 'it' ? 'Spesso' : lang === 'es' ? 'A menudo' : 'Often' },
          { value: 'very_often', label: lang === 'it' ? 'Molto spesso' : lang === 'es' ? 'Muy a menudo' : 'Very often' },
        ];
        return (
          <div className="flex-1 flex flex-col items-center">
            <h1 className="text-2xl font-bold text-[#1A211E] text-center">
              {lang === 'it' ? 'Quanto spesso ti senti troppo stanco per essere presente come vorresti?' : lang === 'es' ? '¿Con qué frecuencia te sientes demasiado cansado para estar presente como quisieras?' : 'How often do you feel too exhausted to show up the way you\'d like?'}
            </h1>
            <div className="flex-1 flex flex-col justify-center w-full">
              <div className="space-y-3 w-full">
                {energyScale.map(o => (
                  <OptionBtn key={o.value} selected={formData.pain_energy === o.value} onClick={() => updateField('pain_energy', o.value)}>
                    {o.label}
                  </OptionBtn>
                ))}
              </div>
            </div>
          </div>
        );
      }

      /* ── 7: Small Gestures (Pain Point) ── */
      case 11: {
        const gesturesScale = [
          { value: 'never', label: lang === 'it' ? 'Mai' : lang === 'es' ? 'Nunca' : 'Never' },
          { value: 'rarely', label: lang === 'it' ? 'Raramente' : lang === 'es' ? 'Raramente' : 'Rarely' },
          { value: 'sometimes', label: lang === 'it' ? 'A volte' : lang === 'es' ? 'A veces' : 'Sometimes' },
          { value: 'often', label: lang === 'it' ? 'Spesso' : lang === 'es' ? 'A menudo' : 'Often' },
          { value: 'very_often', label: lang === 'it' ? 'Molto spesso' : lang === 'es' ? 'Muy a menudo' : 'Very often' },
        ];
        return (
          <div className="flex-1 flex flex-col items-center">
            <h1 className="text-2xl font-bold text-[#1A211E] text-center">
              {lang === 'it' ? 'Quanto spesso i piccoli gesti ti sfuggono?' : lang === 'es' ? '¿Con qué frecuencia se te escapan los pequeños gestos?' : 'How often do small gestures slip through the cracks?'}
            </h1>
            <p className="text-sm text-stone-400 mt-3 text-center leading-relaxed whitespace-pre-line">
              {lang === 'it'
                ? '• ringraziare\n• aiutare senza che venga chiesto\n• notare i piccoli sforzi'
                : lang === 'es'
                ? '• dar las gracias\n• ayudar sin que te lo pidan\n• notar los pequeños esfuerzos'
                : '• saying thank you\n• helping without being asked\n• noticing small efforts'}
            </p>
            <div className="flex-1 flex flex-col justify-center w-full">
              <div className="space-y-3 w-full">
                {gesturesScale.map(o => (
                  <OptionBtn key={o.value} selected={formData.pain_gestures === o.value} onClick={() => updateField('pain_gestures', o.value)}>
                    {o.label}
                  </OptionBtn>
                ))}
              </div>
            </div>
          </div>
        );
      }

      /* ── 8: Connection (Pain Point) ── */
      case 12: {
        const connectionScale = [
          { value: 'very_distant', label: lang === 'it' ? 'Molto distante' : lang === 'es' ? 'Muy distante' : 'Very distant' },
          { value: 'distant', label: lang === 'it' ? 'Distante' : lang === 'es' ? 'Distante' : 'Distant' },
          { value: 'neutral', label: lang === 'it' ? 'Neutro' : lang === 'es' ? 'Neutral' : 'Neutral' },
          { value: 'connected', label: lang === 'it' ? 'Connesso' : lang === 'es' ? 'Conectado' : 'Connected' },
          { value: 'very_connected', label: lang === 'it' ? 'Molto connesso' : lang === 'es' ? 'Muy conectado' : 'Very connected' },
        ];
        return (
          <div className="flex-1 flex flex-col items-center">
            <h1 className="text-2xl font-bold text-[#1A211E] text-center">
              {lang === 'it' ? 'Quanto ti senti connesso con la tua partner?' : lang === 'es' ? '¿Qué tan conectado te sientes con tu pareja?' : 'How connected do you currently feel with your partner?'}
            </h1>
            <div className="flex-1 flex flex-col justify-center w-full">
              <div className="space-y-3 w-full">
                {connectionScale.map(o => (
                  <OptionBtn key={o.value} selected={formData.pain_connection === o.value} onClick={() => updateField('pain_connection', o.value)}>
                    {o.label}
                  </OptionBtn>
                ))}
              </div>
            </div>
          </div>
        );
      }

      /* ── 9: Age ── */
      case 13: {
        const ageOptions = [
          { value: '18-25', label: '18–25' },
          { value: '26-35', label: '26–35' },
          { value: '36-45', label: '36–45' },
          { value: '46+', label: '46+' },
        ];
        return (
          <div className="flex-1 flex flex-col">
            <h1 className="text-2xl font-bold text-[#1A211E] text-center">
              {lang === 'it' ? 'Quanti anni hai?' : lang === 'es' ? '¿Cuántos años tienes?' : 'How old are you?'}
            </h1>
            <div className="flex-1 flex flex-col justify-center">
            <div className="space-y-3">
              {ageOptions.map(o => (
                <OptionBtn key={o.value} selected={formData.age_cluster === o.value} onClick={() => updateField('age_cluster', o.value)}>
                  {o.label}
                </OptionBtn>
              ))}
            </div>
            </div>
          </div>
        );
      }

      /* ── 14: Empathy Moment (adaptive) ── */
      case 14: {
        const empathyVariant = getEmpathyVariant(formData, lang);
        return (
          <div className="flex-1 flex flex-col items-center justify-center text-center px-6">
            <h1 className="text-2xl font-bold text-[#1A211E] text-center">
              {lang === 'it' ? 'Non sei solo.' : lang === 'es' ? 'No estás solo.' : "You're not alone."}
            </h1>
            <p className="text-stone-600 mt-4 leading-relaxed whitespace-pre-line">
              {empathyVariant.bodyLines}
            </p>
            <AnimatePresence>
              {empathyRevealed && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                  className="mt-8"
                >
                  <p className="text-6xl font-bold text-[#1A211E]">
                    <AnimatedCounter
                      target={empathyVariant.statNumber}
                      suffix={empathyVariant.statSuffix}
                      trigger={empathyRevealed}
                      decimals={empathyVariant.decimals}
                    />
                  </p>
                  <p className="text-stone-600 mt-2">
                    {empathyVariant.statDescription}
                  </p>
                  <p className="text-xs text-stone-400 mt-1">{empathyVariant.source}</p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      }

      /* ── 15: Where heard ── */
      case 15: {
        const sources = [
          { value: 'google', label: 'Google', icon: <svg className="w-5 h-5" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09a6.96 6.96 0 010-4.17V7.07H2.18a11.96 11.96 0 000 9.86l3.66-2.84z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg> },
          { value: 'instagram', label: 'Instagram', icon: <svg className="w-5 h-5" viewBox="0 0 24 24"><defs><radialGradient id="ig" cx="30%" cy="107%" r="150%"><stop offset="0%" stopColor="#fdf497"/><stop offset="5%" stopColor="#fdf497"/><stop offset="45%" stopColor="#fd5949"/><stop offset="60%" stopColor="#d6249f"/><stop offset="90%" stopColor="#285AEB"/></radialGradient></defs><rect x="2" y="2" width="20" height="20" rx="5" fill="url(#ig)"/><circle cx="12" cy="12" r="5" fill="none" stroke="#fff" strokeWidth="2"/><circle cx="17.5" cy="6.5" r="1.2" fill="#fff"/></svg> },
          { value: 'reddit', label: 'Reddit', icon: <svg className="w-5 h-5" viewBox="0 0 24 24"><circle cx="12" cy="12" r="12" fill="#FF4500"/><path d="M19.6 12.2a1.6 1.6 0 00-2.7-1.1 8.3 8.3 0 00-4-.9l.8-3.5 2.4.5a1.1 1.1 0 102.2 0 1.1 1.1 0 00-2.1-.5l-2.7-.6a.4.4 0 00-.5.3l-.8 3.8a8.2 8.2 0 00-4.1.9 1.6 1.6 0 10-1.8 2.6 2.5 2.5 0 000 .5c0 2.5 3 4.6 6.6 4.6s6.6-2.1 6.6-4.6a2.5 2.5 0 000-.5 1.6 1.6 0 00.9-1.5zM8.4 13.3a1.1 1.1 0 112.2 0 1.1 1.1 0 01-2.2 0zm6.4 3a4.3 4.3 0 01-2.8.8 4.3 4.3 0 01-2.8-.8.4.4 0 01.5-.5 3.6 3.6 0 002.3.6 3.6 3.6 0 002.3-.6.4.4 0 01.5.5zm-.2-1.9a1.1 1.1 0 110-2.2 1.1 1.1 0 010 2.2z" fill="#fff"/></svg> },
          { value: 'google_play', label: 'Google Play', icon: <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3.5 4.4L16.1 12L3.5 19.6V4.4Z" fill="#34A853"/><path d="M3.5 4.4L12.7 12L3.5 19.6V4.4Z" fill="#4285F4"/><path d="M16.1 12L12.7 12L3.5 4.4L16.1 12Z" fill="#FBBC05"/><path d="M16.1 12L12.7 12L3.5 19.6L16.1 12Z" fill="#EA4335"/></svg> },
          { value: 'apple_app_store', label: 'App Store', icon: <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701"/></svg> },
          { value: 'word_of_mouth', label: lang === 'it' ? 'Passaparola' : lang === 'es' ? 'Boca a boca' : 'Word of mouth', icon: <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg> },
          { value: 'other', label: lang === 'it' ? 'Altro' : lang === 'es' ? 'Otro' : 'Other', icon: <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="1"/><circle cx="19" cy="12" r="1"/><circle cx="5" cy="12" r="1"/></svg> },
        ];
        return (
          <div className="flex-1 flex flex-col">
            <h1 className="text-2xl font-bold text-[#1A211E] text-center">
              {lang === 'it' ? 'Come ci hai trovato?' : lang === 'es' ? '¿Cómo nos encontraste?' : 'Where did you hear about us?'}
            </h1>
            <div className="flex-1 flex flex-col justify-center">
            <div className="space-y-3">
              {sources.map(o => (
                <OptionBtn key={o.value} selected={formData.referral_source === o.value} onClick={() => updateField('referral_source', o.value)}>
                  <span className="flex items-center justify-center gap-3">
                    {o.icon}
                    <span>{o.label}</span>
                  </span>
                </OptionBtn>
              ))}
            </div>
            </div>
          </div>
        );
      }

      /* ── 16: What to improve ── */
      case 16: {
        const areas = [
          { value: 'quality_time', label: lang === 'it' ? 'Avere più tempo di qualità con la mia partner' : lang === 'es' ? 'Tener más tiempo de calidad con mi pareja' : 'Have more quality time with my partner' },
          { value: 'home_management', label: lang === 'it' ? 'Essere più presente nella gestione della casa' : lang === 'es' ? 'Estar más presente en la gestión del hogar' : 'Show up more in home management' },
          { value: 'emotional_presence', label: lang === 'it' ? 'Essere più presente emotivamente' : lang === 'es' ? 'Estar más presente emocionalmente' : 'Be more emotionally present' },
          { value: 'reduce_tension', label: lang === 'it' ? 'Ridurre tensione e stress a casa' : lang === 'es' ? 'Reducir la tensión y el estrés en casa' : 'Reduce tension and stress at home' },
          { value: 'confidence', label: lang === 'it' ? 'Sentirmi più sicuro come partner' : lang === 'es' ? 'Sentirme más seguro como pareja' : 'Feel more confident as a partner' },
        ];
        return (
          <div className="flex-1 flex flex-col items-center">
            <h1 className="text-2xl font-bold text-[#1A211E] text-center">
              {lang === 'it' ? 'Cosa vorresti migliorare?' : lang === 'es' ? '¿Qué te gustaría mejorar?' : 'What would you like to improve?'}
            </h1>
            <p className="text-sm text-[#B3B3B3] mt-2 text-center">
              {lang === 'it' ? 'Seleziona fino a 3 opzioni.' : lang === 'es' ? 'Selecciona hasta 3 opciones.' : 'Select up to 3 options.'}
            </p>
            <div className="flex-1 flex flex-col justify-center w-full">
            <div className="space-y-3 w-full">
              {areas.map(o => (
                <OptionBtn key={o.value} selected={formData.improvement_areas.includes(o.value)} onClick={() => toggleImprovement(o.value)}>
                  <span className="flex items-center justify-center">
                    <span>{o.label}</span>
                    {formData.improvement_areas.includes(o.value) && <span className="text-white">✓</span>}
                  </span>
                </OptionBtn>
              ))}
            </div>
            </div>
          </div>
        );
      }

      /* ── 17: Work Life ── */
      case 17: {
        const jobs = [
          { value: 'freelance', label: 'Freelance' },
          { value: 'part_time', label: 'Part-time' },
          { value: 'full_time', label: lang === 'it' ? 'Tempo pieno' : lang === 'es' ? 'Tiempo completo' : 'Full-time' },
          { value: 'entrepreneur', label: lang === 'it' ? 'Imprenditore' : lang === 'es' ? 'Emprendedor' : 'Entrepreneur' },
          { value: 'other', label: lang === 'it' ? 'Altro' : lang === 'es' ? 'Otro' : 'Other' },
        ];
        return (
          <div className="flex-1 flex flex-col">
            <h1 className="text-2xl font-bold text-[#1A211E] text-center">
              {lang === 'it' ? 'Qual è la tua situazione lavorativa?' : lang === 'es' ? '¿Cuál es tu situación laboral?' : "What's your work situation?"}
            </h1>
            <p className="text-sm text-stone-900 mt-2 text-center">
              {lang === 'it' ? 'Ci aiuta a suggerire gesti contestuali.' : lang === 'es' ? 'Nos ayuda a sugerir gestos contextuales.' : 'This helps us suggest contextual gestures.'}
            </p>
            <div className="flex-1 flex flex-col justify-center">
            <div className="space-y-3">
              {jobs.map(o => (
                <OptionBtn key={o.value} selected={formData.work_schedule === o.value} onClick={() => updateField('work_schedule', o.value)}>
                  {o.label}
                </OptionBtn>
              ))}
            </div>
            </div>
          </div>
        );
      }

      /* ── 18: Time availability ── */
      case 18: {
        const times = [
          { value: 3, label: lang === 'it' ? '1–3 minuti' : lang === 'es' ? '1–3 minutos' : '1–3 minutes' },
          { value: 5, label: lang === 'it' ? '5 minuti' : lang === 'es' ? '5 minutos' : '5 minutes' },
          { value: 10, label: lang === 'it' ? '10 minuti' : lang === 'es' ? '10 minutos' : '10 minutes' },
          { value: -1, label: lang === 'it' ? 'Dipende dal giorno' : lang === 'es' ? 'Depende del día' : 'It depends on the day' },
        ];
        return (
          <div className="flex-1 flex flex-col">
            <h1 className="text-2xl font-bold text-[#1A211E] text-center">
              {lang === 'it' ? 'Quanto tempo puoi dedicare ogni giorno?' : lang === 'es' ? '¿Cuánto tiempo puedes dedicar cada día?' : 'How much time can you dedicate each day?'}
            </h1>
            <div className="flex-1 flex flex-col justify-center">
            <div className="space-y-3">
              {times.map(o => (
                <OptionBtn key={o.value} selected={formData.time_budget_minutes === o.value} onClick={() => updateField('time_budget_minutes', o.value)}>
                  {o.label}
                </OptionBtn>
              ))}
            </div>
            <p className="text-xs text-stone-400 text-center mt-4">
              {lang === 'it' ? 'Anche un minuto può fare la differenza.' : lang === 'es' ? 'Incluso un minuto puede marcar la diferencia.' : 'Even one minute can make a difference.'}
            </p>
            </div>
          </div>
        );
      }

      /* ── 19: Personalized Outcome ── */
      case 19: {
        const primaryArea = formData.improvement_areas[0] || 'emotional_presence';
        const areaHighlight = {
          quality_time: lang === 'it' ? 'più tempo di qualità' : lang === 'es' ? 'más tiempo de calidad' : 'more quality time',
          home_management: lang === 'it' ? 'più presenza in casa' : lang === 'es' ? 'más presencia en casa' : 'more presence at home',
          emotional_presence: lang === 'it' ? 'più presenza emotiva' : lang === 'es' ? 'más presencia emocional' : 'more emotional presence',
          reduce_tension: lang === 'it' ? 'meno tensione a casa' : lang === 'es' ? 'menos tensión en casa' : 'less tension at home',
          confidence: lang === 'it' ? 'più sicurezza come partner' : lang === 'es' ? 'más seguridad como pareja' : 'more confidence as a partner',
        };
        const highlight = areaHighlight[primaryArea] || areaHighlight.emotional_presence;
        return (
          <div className="flex-1 flex flex-col items-center justify-center text-center px-6">
            <motion.h1
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-[28px] font-bold text-[#1A211E] leading-tight"
            >
              {lang === 'it' ? 'Raggiungere ' : lang === 'es' ? 'Alcanzar ' : 'Achieving '}
              <span style={{ color: '#E36A55' }}>{highlight}</span>
              {lang === 'it' ? ' è un obiettivo realistico. E non è difficile.' : lang === 'es' ? ' es un objetivo realista. Y no es difícil.' : ' is a realistic target. And it\'s not hard.'}
            </motion.h1>

            <AnimatePresence>
              {outcomeRevealed && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                  className="w-full mt-8"
                >
                  <p className="text-stone-900 text-[15px] leading-relaxed max-w-[320px] mx-auto">
                    {lang === 'it'
                      ? 'L\'87% degli utenti nota un miglioramento nella propria relazione dopo aver usato Better Husband per 7 giorni. I piccoli gesti quotidiani si accumulano.'
                      : lang === 'es'
                      ? 'El 87% de los usuarios nota una mejora en su relación después de usar Better Husband durante 7 días. Los pequeños gestos diarios se acumulan.'
                      : '87% of users notice an improvement in their relationship after using Better Husband for 7 days. Small daily gestures compound.'}
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      }

      /* ── 20: Terms & Privacy ── */
      case 20:
        return (
          <div className="flex-1 flex flex-col items-center justify-center text-center">
            {/* Trust icon – sun/shield */}
            <div className="w-28 h-28 rounded-full flex items-center justify-center mb-6" style={{ background: '#F5A623' }}>
              <svg viewBox="0 0 64 64" className="w-16 h-16" fill="none" xmlns="http://www.w3.org/2000/svg">
                {/* Sun rays */}
                {[0, 45, 90, 135, 180, 225, 270, 315].map(deg => (
                  <line
                    key={deg}
                    x1="32" y1="4" x2="32" y2="12"
                    stroke="#000" strokeWidth="2" strokeLinecap="round"
                    transform={`rotate(${deg} 32 32)`}
                  />
                ))}
                {/* Sun body */}
                <circle cx="32" cy="32" r="13" stroke="#000" strokeWidth="2" />
                {/* Shield inside */}
                <path d="M32 24 L38 27 L38 33 C38 36.5 35.5 39.5 32 41 C28.5 39.5 26 36.5 26 33 L26 27 Z" stroke="#000" strokeWidth="1.5" strokeLinejoin="round" />
                <path d="M29.5 32 L31 33.5 L34.5 30" stroke="#000" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>

            <h1 className="text-2xl font-bold text-[#1A211E] text-center">
              {lang === 'it' ? 'Grazie per la tua fiducia' : lang === 'es' ? 'Gracias por tu confianza' : 'Thank you for trusting us'}
            </h1>
            <p className="text-[#1A211E] mt-3 text-sm leading-relaxed text-center">
              {lang === 'it'
                ? 'La tua privacy e sicurezza sono importanti per noi.\nManteniamo sempre i tuoi dati privati e al sicuro.'
                : lang === 'es'
                ? 'Tu privacidad y seguridad nos importan.\nProtegeremos siempre tu información personal de forma privada y segura.'
                : 'Your privacy and security matter to us.\nWe promise to always keep your personal information private and secure.'}
            </p>

            <div className="mt-8 w-full space-y-4 flex flex-col items-center">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.accepted_terms}
                  onChange={e => setFormData(prev => ({ ...prev, accepted_terms: e.target.checked }))}
                  className="mt-0.5 w-5 h-5 rounded border-[#1A211E] bg-white focus:ring-[#1A211E] accent-black flex-shrink-0"
                />
                <span className="text-sm text-[#1A211E]">
                  {lang === 'it' ? (
                    <>Accetto i <button type="button" onClick={(e) => { e.preventDefault(); setLegalModal('terms'); }} className="underline font-medium text-[#1A211E]">Termini di Servizio</button></>
                  ) : lang === 'es' ? (
                    <>Acepto los <button type="button" onClick={(e) => { e.preventDefault(); setLegalModal('terms'); }} className="underline font-medium text-[#1A211E]">Términos de Servicio</button></>
                  ) : (
                    <>I agree to the <button type="button" onClick={(e) => { e.preventDefault(); setLegalModal('terms'); }} className="underline font-medium text-[#1A211E]">Terms of Service</button></>
                  )}
                </span>
              </label>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.accepted_privacy}
                  onChange={e => setFormData(prev => ({ ...prev, accepted_privacy: e.target.checked }))}
                  className="mt-0.5 w-5 h-5 rounded border-[#1A211E] bg-white focus:ring-[#1A211E] accent-black flex-shrink-0"
                />
                <span className="text-sm text-[#1A211E]">
                  {lang === 'it' ? (
                    <>Accetto la <button type="button" onClick={(e) => { e.preventDefault(); setLegalModal('privacy'); }} className="underline font-medium text-[#1A211E]">Privacy Policy</button></>
                  ) : lang === 'es' ? (
                    <>Acepto la <button type="button" onClick={(e) => { e.preventDefault(); setLegalModal('privacy'); }} className="underline font-medium text-[#1A211E]">Política de Privacidad</button></>
                  ) : (
                    <>I agree to the <button type="button" onClick={(e) => { e.preventDefault(); setLegalModal('privacy'); }} className="underline font-medium text-[#1A211E]">Privacy Policy</button></>
                  )}
                </span>
              </label>
            </div>
          </div>
        );

      /* ── 21: Personalization Loading ── */
      case 21: {
        // Generate personalized roadmap based on ALL user answers
        const painScoresLocal = { never: 0, rarely: 1, sometimes: 2, often: 3, very_often: 4 };
        const connectionScoresLocal = { very_connected: 0, connected: 1, neutral: 2, distant: 3, very_distant: 4 };
        const pScores = {
          autopilot: painScoresLocal[formData.pain_autopilot] ?? 0,
          energy: painScoresLocal[formData.pain_energy] ?? 0,
          gestures: painScoresLocal[formData.pain_gestures] ?? 0,
          connection: connectionScoresLocal[formData.pain_connection] ?? 0,
        };
        const sorted = Object.entries(pScores).sort((a, b) => b[1] - a[1]);
        const topPain = sorted[0][0];
        const secondPain = sorted[1][0];

        const l = lang === 'it' ? 'it' : lang === 'es' ? 'es' : 'en';

        // ── Deeply personalized content using every onboarding answer ──
        const timeBudget = formData.time_budget_minutes || 10;
        const hasKids = formData.has_kids === 'yes';
        const kidsAges = formData.kids_ages || [];
        const hasSmallKids = kidsAges.some(a => a === '0-2' || a === '3-5');
        const workSchedule = formData.work_schedule;
        const improvementAreas = formData.improvement_areas || [];
        const relationship = formData.relationship_status;

        // Time-aware phrasing
        const timePhrase = {
          it: timeBudget <= 5 ? 'micro-gesti da 5 minuti' : timeBudget <= 10 ? 'rituali da 10 minuti' : 'rituali quotidiani dedicati',
          en: timeBudget <= 5 ? '5-minute micro-gestures' : timeBudget <= 10 ? '10-minute daily rituals' : 'dedicated daily rituals',
          es: timeBudget <= 5 ? 'micro-gestos de 5 minutos' : timeBudget <= 10 ? 'rituales diarios de 10 minutos' : 'rituales diarios dedicados',
        };

        // Schedule-aware context
        const scheduleContext = {
          it: workSchedule === 'remote' ? 'durante le pause da casa'
            : workSchedule === 'shift_work' ? 'adattati ai tuoi turni'
            : workSchedule === 'regular_9_5' ? 'prima e dopo il lavoro'
            : 'nei momenti che scegli tu',
          en: workSchedule === 'remote' ? 'during breaks at home'
            : workSchedule === 'shift_work' ? 'adapted to your shifts'
            : workSchedule === 'regular_9_5' ? 'before and after work'
            : 'at the times you choose',
          es: workSchedule === 'remote' ? 'durante las pausas en casa'
            : workSchedule === 'shift_work' ? 'adaptados a tus turnos'
            : workSchedule === 'regular_9_5' ? 'antes y después del trabajo'
            : 'en los momentos que tú elijas',
        };

        // Kids-aware phrasing
        const kidsContext = {
          it: hasSmallKids ? ', anche con bimbi piccoli' : hasKids ? ', compatibili con la famiglia' : '',
          en: hasSmallKids ? ', even with little ones around' : hasKids ? ', family-compatible' : '',
          es: hasSmallKids ? ', incluso con pequeños en casa' : hasKids ? ', compatibles con la familia' : '',
        };

        // Phase 1: top pain + time + schedule + kids
        const phase1Titles = {
          autopilot: { it: 'Uscire dal pilota automatico', en: 'Breaking the autopilot', es: 'Salir del piloto automático' },
          energy: { it: "Gestione dell'energia", en: 'Energy management', es: 'Gestión de la energía' },
          gestures: { it: 'Micro-gesti quotidiani', en: 'Daily micro-gestures', es: 'Micro-gestos diarios' },
          connection: { it: 'Riconnessione emotiva', en: 'Emotional reconnection', es: 'Reconexión emocional' },
        };
        const phase1Descs = {
          autopilot: {
            it: `${timePhrase.it} per tornare presente ${scheduleContext.it}${kidsContext.it}`,
            en: `${timePhrase.en} to become present ${scheduleContext.en}${kidsContext.en}`,
            es: `${timePhrase.es} para volver a estar presente ${scheduleContext.es}${kidsContext.es}`,
          },
          energy: {
            it: `${timePhrase.it} pensati per i momenti di stanchezza ${scheduleContext.it}${kidsContext.it}`,
            en: `${timePhrase.en} designed for low-energy moments ${scheduleContext.en}${kidsContext.en}`,
            es: `${timePhrase.es} pensados para los momentos de cansancio ${scheduleContext.es}${kidsContext.es}`,
          },
          gestures: {
            it: `Costruire l'abitudine con ${timePhrase.it} ${scheduleContext.it}${kidsContext.it}`,
            en: `Building the habit with ${timePhrase.en} ${scheduleContext.en}${kidsContext.en}`,
            es: `Construir el hábito con ${timePhrase.es} ${scheduleContext.es}${kidsContext.es}`,
          },
          connection: {
            it: `Gesti mirati per rafforzare il legame con ${timePhrase.it}${kidsContext.it}`,
            en: `Targeted gestures to strengthen your bond with ${timePhrase.en}${kidsContext.en}`,
            es: `Gestos enfocados para fortalecer el vínculo con ${timePhrase.es}${kidsContext.es}`,
          },
        };

        // Phase 2: second pain + improvement areas
        const phase2Titles = {
          autopilot: { it: 'Presenza attiva', en: 'Active presence', es: 'Presencia activa' },
          energy: { it: 'Energia condivisa', en: 'Shared energy', es: 'Energía compartida' },
          gestures: { it: 'Gesti che contano', en: 'Gestures that matter', es: 'Gestos que cuentan' },
          connection: { it: 'Connessione profonda', en: 'Deeper connection', es: 'Conexión profunda' },
        };
        const improvementLabelsMap = {
          quality_time: { it: 'tempo di qualità', en: 'quality time', es: 'tiempo de calidad' },
          home_management: { it: 'gestione della casa', en: 'home management', es: 'gestión del hogar' },
          emotional_presence: { it: 'presenza emotiva', en: 'emotional presence', es: 'presencia emocional' },
          reduce_tension: { it: 'riduzione tensioni', en: 'tension reduction', es: 'reducción de tensión' },
          confidence: { it: 'sicurezza come partner', en: 'partner confidence', es: 'seguridad como pareja' },
        };
        const topAreas = improvementAreas.slice(0, 2).map(a => improvementLabelsMap[a]?.[l] || a);
        const areasText = topAreas.length > 0
          ? (lang === 'it' ? `Focus su ${topAreas.join(' e ')}` : lang === 'es' ? `Enfoque en ${topAreas.join(' y ')}` : `Focusing on ${topAreas.join(' and ')}`)
          : (lang === 'it' ? 'Approfondimento delle abitudini' : lang === 'es' ? 'Profundización de los hábitos' : 'Deepening your habits');

        // Phase 3: relationship-status-aware growth goal
        const relationshipGoal = {
          married: { it: 'Rafforzare il matrimonio ogni giorno', en: 'Strengthen your marriage daily', es: 'Fortalecer el matrimonio cada día' },
          living_together: { it: 'Crescere insieme nella convivenza', en: 'Grow together in daily life', es: 'Crecer juntos en la convivencia' },
          dating: { it: 'Costruire basi solide per il futuro', en: 'Build a strong foundation', es: 'Construir bases sólidas para el futuro' },
          engaged: { it: 'Prepararsi al meglio per la vita insieme', en: 'Prepare for life together', es: 'Prepararse para la vida juntos' },
          complicated: { it: 'Ricostruire fiducia e connessione', en: 'Rebuild trust and connection', es: 'Reconstruir la confianza y la conexión' },
        };
        const phase3Title = relationshipGoal[relationship]?.[l] || (lang === 'it' ? 'Crescita continua' : lang === 'es' ? 'Crecimiento continuo' : 'Continuous growth');
        const phase3Desc = lang === 'it'
          ? `Il piano evolve con te — ${timeBudget} min/giorno, adattato ai tuoi progressi`
          : lang === 'es'
          ? `El plan evoluciona contigo — ${timeBudget} min/día, adaptado a tu progreso`
          : `Your plan evolves with you — ${timeBudget} min/day, adapted to your progress`;

        const phaseLabels = phase1Titles;

        const roadmapPhases = [
          {
            week: lang === 'it' ? 'Settimana 1' : lang === 'es' ? 'Semana 1' : 'Week 1',
            title: phase1Titles[topPain]?.[l] || (lang === 'it' ? 'Fondamenta' : lang === 'es' ? 'Cimientos' : 'Foundation'),
            desc: phase1Descs[topPain]?.[l] || '',
          },
          {
            week: lang === 'it' ? 'Settimana 2–3' : lang === 'es' ? 'Semana 2–3' : 'Week 2–3',
            title: phase2Titles[secondPain]?.[l] || (lang === 'it' ? 'Costruire presenza' : lang === 'es' ? 'Construir presencia' : 'Building presence'),
            desc: areasText,
          },
          {
            week: lang === 'it' ? 'Settimana 4+' : lang === 'es' ? 'Semana 4+' : 'Week 4+',
            title: phase3Title,
            desc: phase3Desc,
          },
        ];

        const improvementLabels = {
          quality_time: { it: 'Tempo di qualità', en: 'Quality time', es: 'Tiempo de calidad' },
          home_management: { it: 'Gestione della casa', en: 'Home management', es: 'Gestión del hogar' },
          emotional_presence: { it: 'Presenza emotiva', en: 'Emotional presence', es: 'Presencia emocional' },
          reduce_tension: { it: 'Riduzione tensione', en: 'Tension reduction', es: 'Reducción de tensión' },
          confidence: { it: 'Sicurezza come partner', en: 'Partner confidence', es: 'Seguridad como pareja' },
        };

        const checklistItems = [
          ...formData.improvement_areas.map(a => ({
            key: a,
            label: improvementLabels[a]?.[l] || a,
          })),
          {
            key: 'daily_suggestion',
            label: lang === 'it' ? 'Suggerimento giornaliero personalizzato' : lang === 'es' ? 'Sugerencia diaria personalizada' : 'Personalized daily suggestion',
          },
        ];

        const statusMessages = [
          lang === 'it' ? 'Analisi delle tue risposte...' : lang === 'es' ? 'Analizando tus respuestas...' : 'Analyzing your answers...',
          lang === 'it' ? 'Creazione del piano personalizzato...' : lang === 'es' ? 'Creando tu plan personalizado...' : 'Creating your personalized plan...',
          lang === 'it' ? `Ottimizzazione per: ${phaseLabels[topPain]?.[l] || ''}` : lang === 'es' ? `Optimizando para: ${phaseLabels[topPain]?.es || phaseLabels[topPain]?.en || ''}` : `Optimizing for: ${phaseLabels[topPain]?.en || ''}`,
          lang === 'it' ? 'Selezione dei gesti per la settimana 1...' : lang === 'es' ? 'Seleccionando los gestos para la semana 1...' : 'Selecting gestures for week 1...',
          lang === 'it' ? 'Calibrazione della difficoltà...' : lang === 'es' ? 'Calibrando el nivel de dificultad...' : 'Calibrating difficulty level...',
          lang === 'it' ? 'Finalizzazione...' : lang === 'es' ? 'Finalizando...' : 'Finalizing...',
        ];

        const PersonalizationContent = () => {
          const [loadProgress, setLoadProgress] = React.useState(0);
          const [statusIndex, setStatusIndex] = React.useState(0);
          const [checkedItems, setCheckedItems] = React.useState([]);

          React.useEffect(() => {
            const interval = setInterval(() => {
              setLoadProgress(prev => {
                if (prev >= 100) { clearInterval(interval); return 100; }
                return prev + 1;
              });
            }, 70);
            return () => clearInterval(interval);
          }, []);

          React.useEffect(() => {
            const timers = statusMessages.map((_, i) =>
              i > 0 ? setTimeout(() => setStatusIndex(i), i * 1200) : null
            ).filter(Boolean);
            return () => timers.forEach(clearTimeout);
          }, []); // eslint-disable-line react-hooks/exhaustive-deps

          React.useEffect(() => {
            const timers = checklistItems.map((item, i) =>
              setTimeout(() => setCheckedItems(prev => [...prev, item.key]), 2000 + i * 1000)
            );
            return () => timers.forEach(clearTimeout);
          }, []); // eslint-disable-line react-hooks/exhaustive-deps

          return (
            <div className="flex-1 flex flex-col items-center justify-center text-center">
              <motion.p
                className="text-5xl font-bold text-[#1A211E] mb-2"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4 }}
              >
                {loadProgress}%
              </motion.p>

              <p className="text-lg font-medium text-[#1A211E] mb-1">
                {lang === 'it' ? 'Stiamo creando il tuo piano' : lang === 'es' ? 'Estamos creando tu plan' : "We're setting everything up for you"}
              </p>

              <div className="w-full max-w-xs mt-4 mb-3">
                <div className="w-full h-2 bg-[#2E2E36] rounded-full overflow-hidden">
                  <motion.div
                    className="h-full rounded-full"
                    style={{ background: '#E36A55' }}
                    initial={{ width: 0 }}
                    animate={{ width: '100%' }}
                    transition={{ duration: 7, ease: 'linear' }}
                  />
                </div>
              </div>

              <AnimatePresence mode="wait">
                <motion.p
                  key={statusIndex}
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -5 }}
                  transition={{ duration: 0.3 }}
                  className="text-sm text-[#1A211E]"
                >
                  {statusMessages[statusIndex]}
                </motion.p>
              </AnimatePresence>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.5 }}
                className="w-full max-w-xs mt-8 bg-white/90 rounded-xl p-5 text-left"
              >
                <p className="text-sm font-semibold text-[#1A211E] mb-3">
                  {lang === 'it' ? 'Il tuo piano include' : lang === 'es' ? 'Tu plan incluye' : 'Your plan includes'}
                </p>
                <div className="space-y-2.5">
                  {checklistItems.map(item => {
                    const isChecked = checkedItems.includes(item.key);
                    return (
                      <div key={item.key} className="flex items-center gap-2.5">
                        <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-300 ${
                          isChecked ? '' : 'bg-[#2E382E]'
                        }`}
                        style={isChecked ? { background: '#1c1917' } : {}}
                        >
                          {isChecked && (
                            <motion.svg
                              initial={{ scale: 0 }}
                              animate={{ scale: 1 }}
                              className="w-3 h-3 text-white"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              strokeWidth={3}
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </motion.svg>
                          )}
                        </div>
                        <span className="text-sm text-[#1A211E] transition-colors duration-300">
                          {item.label}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </motion.div>
            </div>
          );
        };

        if (personalizationDone) {
          const phaseIcons = [
            // Week 1 — seedling
            <svg key="i1" className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M12 22V12" /><path d="M12 12C12 12 7 9 7 5c0-2.5 2.5-4 5-4" /><path d="M12 12c0 0 5-3 5-7 0-2.5-2.5-4-5-4" /></svg>,
            // Week 2-3 — heart pulse
            <svg key="i2" className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M3 12h4l3-9 4 18 3-9h4" /></svg>,
            // Week 4+ — trophy
            <svg key="i3" className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M6 9H4.5a2.5 2.5 0 010-5H6" /><path d="M18 9h1.5a2.5 2.5 0 000-5H18" /><path d="M4 22h16" /><path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20 7 22" /><path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20 17 22" /><path d="M18 2H6v7a6 6 0 0012 0V2z" /></svg>,
          ];
          const phaseColors = ['#5C8748', '#D4A043', '#C07B30'];
          const phaseBorders = ['rgba(92, 135, 72, 0.4)', 'rgba(212, 160, 67, 0.3)', 'rgba(192, 123, 48, 0.3)'];

          return (
            <div className="flex-1 flex flex-col items-center text-center">
              {/* Checkmark */}
              <motion.div
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: 'spring', stiffness: 200, damping: 15 }}
                className="w-14 h-14 rounded-full flex items-center justify-center mb-5"
                style={{ background: '#000' }}
              >
                <svg className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </motion.div>

              {/* Title */}
              <motion.h1
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="text-[22px] font-bold text-[#1A211E] leading-tight"
              >
                {lang === 'it' ? 'Il tuo piano personalizzato\nè pronto!' : lang === 'es' ? 'Tu plan personalizado\nestá listo!' : 'Your Personal Plan\nIs Ready'}
              </motion.h1>

              {/* Subtitle */}
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.35 }}
                className="text-[13px] text-[#1A211E] mt-2 max-w-[280px] leading-relaxed"
              >
                {lang === 'it'
                  ? 'Abbiamo creato un percorso su misura in base alle tue risposte.'
                  : lang === 'es'
                  ? 'Hemos creado un camino a medida según tus respuestas.'
                  : "We've crafted a path tailored to your specific answers."}
              </motion.p>

              {/* Timeline with cards */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="w-full max-w-sm mt-8"
              >
                {roadmapPhases.map((phase, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.7 + i * 0.25 }}
                    className="flex items-stretch gap-3"
                  >
                    {/* Icon + connector column */}
                    <div className="flex flex-col items-center" style={{ width: 40 }}>
                      <div
                        className="w-10 h-10 rounded-full flex items-center justify-center text-white flex-shrink-0 shadow-lg"
                        style={{ background: '#000' }}
                      >
                        {phaseIcons[i]}
                      </div>
                      {i < roadmapPhases.length - 1 && (
                        <div className="flex-1 w-[2px] my-1" style={{ background: `linear-gradient(to bottom, ${phaseColors[i]}, ${phaseColors[i + 1]})` }} />
                      )}
                    </div>

                    {/* Card */}
                    <div
                      className="flex-1 mb-3 rounded-xl text-left"
                      style={{
                        border: '1px solid rgba(0,0,0,0.08)',
                        background: '#ffffff',
                        padding: '12px 14px',
                      }}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-[10px] font-bold uppercase tracking-widest text-[#1A211E]">{phase.week}</p>
                        {i === 0 && (
                          <span className="text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full" style={{ background: '#F5A623', color: '#1A211E' }}>
                            {lang === 'it' ? 'Attuale' : lang === 'es' ? 'Actual' : 'Current'}
                          </span>
                        )}
                      </div>
                      <p className="text-[14px] font-bold text-[#1A211E] leading-tight">{phase.title}</p>
                      <p className="text-[12px] text-[#1A211E] mt-1 leading-snug">{phase.desc}</p>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          );
        }

        return <PersonalizationContent />;
      }

      /* ── 22: Paywall Step 1 — What you're getting ──
         No longer "try it for free": there is no trial to offer, so promising
         one here would be a false claim (and an App Store 3.1.2 problem). */
      case 22: {
        return (
          <div className="flex-1 flex flex-col items-center justify-center text-center relative">

            <motion.h1
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-[28px] font-bold text-[#1A211E] leading-tight mt-4"
            >
              {lang === 'it'
                ? 'Ecco cosa fa\nBetter Husband\nper te.'
                : lang === 'es'
                ? 'Esto es lo que\nBetter Husband\nhace por ti.'
                : 'See what Better Husband\ndoes for you.'}
            </motion.h1>

            {/* App Preview Screenshot */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="mt-8 w-full mx-auto relative" style={{ maxHeight: '50vh', overflow: 'hidden' }}
            >
              <img
                src={dashboardPortrait}
                alt="Better Husband App"
                className="w-full object-cover object-top rounded-2xl"
                style={{ maxHeight: '50vh' }}
              />
              <div
                className="absolute bottom-0 left-0 right-0 h-24"
                style={{ background: 'linear-gradient(to bottom, transparent, hsl(var(--background)))' }}
              />
            </motion.div>

            {/* One purchase, stated up front — replaces "No Payment Due Now",
                which only made sense while a trial deferred the charge. */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="flex items-center gap-2 mt-8"
            >
              <svg className="w-5 h-5 text-[#A8E6A1]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
              <span className="text-sm font-medium text-[#1A211E]">
                {lang === 'it' ? 'Un pagamento, per sempre' : lang === 'es' ? 'Un pago, para siempre' : 'One payment, forever'}
              </span>
            </motion.div>

            {/* Betatester bypass */}
            {user && (user.role === 'admin' || user.role === 'betatester') && (
              <motion.button
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6 }}
                className="mt-4 px-6 py-2 border border-stone-300 rounded-full text-xs font-medium text-stone-900 hover:bg-stone-100 transition-colors"
                onClick={() => navigate('/dashboard', { replace: true })}
              >
                I'm a betatester
              </motion.button>
            )}
          </div>
        );
      }

      /* ── 23: Paywall Step 2 — No-surprises reassurance ──
         Was the "we'll remind you before your trial ends" screen. With the
         trial retired there is nothing to remind anyone about, so the step is
         repurposed rather than removed — the step numbering is part of the
         paywall deep link (/onboarding?step=paywall&paywall_step=24) and of
         totalSteps, and renumbering would break existing links. */
      case 23: {
        return (
          <div className="flex-1 flex flex-col items-center justify-center text-center relative">

            <motion.h1
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-[28px] font-bold text-[#1A211E] leading-tight"
            >
              {lang === 'it'
                ? 'Paghi una volta.\nNiente abbonamenti,\nniente rinnovi,\nnessuna sorpresa'
                : lang === 'es'
                ? 'Pagas una vez.\nSin suscripciones,\nsin renovaciones,\nsin sorpresas'
                : 'You pay once.\nNo subscription,\nno renewals,\nno surprises'}
            </motion.h1>

            {/* Bell Icon with notification badge */}
            <motion.div
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: 'spring', stiffness: 200, damping: 15, delay: 0.2 }}
              className="relative mt-10"
            >
              <svg className="w-20 h-20 text-stone-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
              {/* Red notification badge */}
              <div className="absolute -top-1 -right-1 w-8 h-8 bg-red-500 rounded-full flex items-center justify-center shadow-lg">
                <span className="text-white text-sm font-bold">1</span>
              </div>
            </motion.div>

            {/* No Payment Due */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="flex items-center gap-2 mt-12"
            >
              <svg className="w-5 h-5 text-[#A8E6A1]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
              <span className="text-sm font-medium text-[#1A211E]">
                {lang === 'it' ? 'Un solo pagamento' : lang === 'es' ? 'Un solo pago' : 'A single payment'}
              </span>
            </motion.div>
          </div>
        );
      }

      /* ── 24: Paywall Step 3 — the lifetime offer ── */
      case 24: {
        const orangeIcon = { background: 'rgba(245, 166, 35, 0.15)', border: '1.5px solid #F5A623' };
        const greenIcon = { background: 'rgba(109, 179, 63, 0.15)', border: '1.5px solid #6DB33F' };

        // What you get and when — replaces the old trial countdown
        // ("Today / Day 5 / Day 7 you get billed"), which described a trial
        // that no longer exists. The shape is deliberately unchanged; only
        // what each step says has moved from "when we charge you" to "what
        // you keep".
        const trialTimeline = [
          {
            icon: (
              <div className="w-9 h-9 rounded-full flex items-center justify-center" style={orangeIcon}>
                {/* Solar: Lock Unlocked (Linear) */}
                <svg className="w-[18px] h-[18px]" viewBox="0 0 24 24" fill="none">
                  <path d="M2 16c0-2.828 0-4.243.879-5.121C3.757 10 5.172 10 8 10h8c2.828 0 4.243 0 5.121.879C22 11.757 22 13.172 22 16c0 2.828 0 4.243-.879 5.121C20.243 22 18.828 22 16 22H8c-2.828 0-4.243 0-5.121-.879C2 20.243 2 18.828 2 16z" stroke="#F5A623" strokeWidth={1.5} />
                  <path d="M6 10V8a6 6 0 0112 0v2" stroke="#F5A623" strokeWidth={1.5} strokeLinecap="round" />
                  <circle cx="12" cy="16" r="2" fill="#F5A623" />
                </svg>
              </div>
            ),
            lineColor: '#F5A623',
            title: lang === 'it' ? 'Oggi' : lang === 'es' ? 'Hoy' : 'Today',
            desc: lang === 'it'
              ? 'Accesso completo: piano giornaliero personalizzato, suggerimenti adattivi e check-in quotidiani.'
              : lang === 'es'
              ? 'Acceso completo: plan diario personalizado, sugerencias adaptativas y check-ins diarios.'
              : 'Full access: personalized daily plan, adaptive suggestions, and daily check-ins.',
          },
          {
            icon: (
              <div className="w-9 h-9 rounded-full flex items-center justify-center" style={orangeIcon}>
                {/* Solar: Bell (Linear) */}
                <svg className="w-[18px] h-[18px]" viewBox="0 0 24 24" fill="none">
                  <path d="M18.75 9.71v-.705C18.75 5.136 15.726 2 12 2S5.25 5.136 5.25 9.005v.705a4.4 4.4 0 01-.692 2.375L3.45 13.81c-1.011 1.575-.239 3.716 1.52 4.214a25.775 25.775 0 0014.06 0c1.759-.498 2.531-2.639 1.52-4.213l-1.108-1.725a4.4 4.4 0 01-.692-2.375z" stroke="#F5A623" strokeWidth={1.5} />
                  <path d="M7.5 19c.655 1.748 2.422 3 4.5 3s3.845-1.252 4.5-3" stroke="#F5A623" strokeWidth={1.5} strokeLinecap="round" />
                </svg>
              </div>
            ),
            lineColor: '#F5A623',
            title: lang === 'it' ? 'Ogni giorno' : lang === 'es' ? 'Cada día' : 'Every day',
            desc: lang === 'it'
              ? 'Il motore impara il tuo contesto e affina i suggerimenti. Ogni aggiornamento è incluso.'
              : lang === 'es'
              ? 'El motor aprende tu contexto y afina las sugerencias. Cada actualización está incluida.'
              : 'The engine learns your context and sharpens its suggestions. Every update is included.',
          },
          {
            icon: (
              <div className="w-9 h-9 rounded-full flex items-center justify-center" style={greenIcon}>
                {/* Solar: Card (Linear) */}
                <svg className="w-[18px] h-[18px]" viewBox="0 0 24 24" fill="none">
                  <path d="M2 12c0-3.771 0-5.657 1.172-6.828C4.343 4 6.229 4 10 4h4c3.771 0 5.657 0 6.828 1.172C22 6.343 22 8.229 22 12c0 3.771 0 5.657-1.172 6.828C19.657 20 17.771 20 14 20h-4c-3.771 0-5.657 0-6.828-1.172C2 17.657 2 15.771 2 12z" stroke="#6DB33F" strokeWidth={1.5} />
                  <path d="M10 16H6" stroke="#6DB33F" strokeWidth={1.5} strokeLinecap="round" />
                  <path d="M14 16h-1.5" stroke="#6DB33F" strokeWidth={1.5} strokeLinecap="round" />
                  <path d="M2 10h20" stroke="#6DB33F" strokeWidth={1.5} />
                </svg>
              </div>
            ),
            lineColor: '#6DB33F',
            title: lang === 'it' ? 'Mai più' : lang === 'es' ? 'Nunca más' : 'Never again',
            desc: lang === 'it'
              ? 'Nessun altro addebito. Nessun abbonamento da gestire, niente da annullare.'
              : lang === 'es'
              ? 'Ningún cobro más. Sin suscripción que gestionar, nada que cancelar.'
              : 'No further charge. No subscription to manage, nothing to cancel.',
          },
        ];

        return (
          <div className="flex-1 flex flex-col text-center relative justify-between">

            <motion.h1
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-[24px] font-extrabold text-[#1A211E] leading-[1.2] mt-4 px-2"
              style={{ letterSpacing: '-0.02em' }}
            >
              {lang === 'it'
                ? 'Sblocca Better Husband per sempre.'
                : lang === 'es'
                ? 'Desbloquea Better Husband para siempre.'
                : 'Unlock Better Husband for good.'}
            </motion.h1>

            {/* Trial Timeline */}
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="mt-8 w-full"
            >
              {trialTimeline.map((item, i) => (
                <div key={i} className="flex gap-3 text-left" style={{ alignItems: 'stretch' }}>
                  <div className="flex flex-col items-center" style={{ minHeight: '100%' }}>
                    {item.icon}
                    {i < trialTimeline.length - 1 && (
                      <div className="w-[2px] rounded-full" style={{ flex: 1, minHeight: 16, background: `linear-gradient(to bottom, ${item.lineColor}, ${trialTimeline[i + 1].lineColor || item.lineColor})` }} />
                    )}
                  </div>
                  <div className="pt-1.5 pb-5 flex-1">
                    <p className="text-[13px] font-semibold text-[#1A211E] tracking-wide">{item.title}</p>
                    <p className="text-[12px] text-[rgba(26,33,30,0.65)] mt-0.5 leading-snug">{item.desc}</p>
                  </div>
                </div>
              ))}
            </motion.div>

            {/* Social proof sits between what you get and what it costs:
                the last thing read before the price is that other men are
                already doing this. Renders nothing until the live count
                arrives — see SocialProof. */}
            <SocialProof lang={lang} className="mt-2" />

            {/* The offer — one product, bought once. There is no plan to
                choose and no price to hardcode: the amount comes from the
                store on native and from Stripe on web. */}
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="mt-8"
            >
              <div
                data-testid="lifetime-plan"
                className="p-5 rounded-2xl border-2 text-center relative"
                style={{ borderColor: '#3B82F6', background: 'rgba(59,130,246,0.08)' }}
              >
                <p className="text-[13px] font-semibold text-[#1A211E]">
                  {lang === 'it' ? 'Accesso a vita' : lang === 'es' ? 'Acceso de por vida' : 'Lifetime access'}
                </p>
                <p className="text-[32px] font-bold text-[#1A211E] mt-1 leading-none">
                  {lifetimePrice || '…'}
                </p>
                <p className="text-[12px] font-normal text-[rgba(26,33,30,0.6)] mt-2">
                  {lang === 'it' ? 'Paghi una volta. È tuo per sempre.'
                    : lang === 'es' ? 'Paga una vez. Es tuyo para siempre.'
                    : 'Pay once. Yours forever.'}
                </p>
              </div>
            </motion.div>

            {/* One-time terms — replaces the old "No Payment Due Now" trial line */}
            <div className="flex items-center justify-center gap-2 mt-6 mb-2">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="rgba(255,255,255,0.3)" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
              <span className="text-[13px] font-medium text-[rgba(26,33,30,0.45)]">
                {lang === 'it' ? 'Pagamento unico — nessun abbonamento'
                  : lang === 'es' ? 'Pago único — sin suscripción'
                  : 'One-time payment — no subscription'}
              </span>
            </div>
          </div>
        );
      }

      default:
        return null;
    }
  };

  /* ── Transition screen ───────────────────────────────────────── */
  if (showTransition) {
    return (
      <div className="relative flex flex-col items-center justify-center min-h-screen pb-safe" style={{
        backgroundColor: '#F4EFE8',
      }}>
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.4 }} className="relative text-center space-y-6 px-8">
          <div className="w-10 h-10 border-2 border-stone-600 border-t-stone-400 rounded-full animate-spin mx-auto" />
          <p className="text-lg text-stone-900 font-manrope">
            {lang === 'it' ? 'Stiamo configurando la tua esperienza…' : lang === 'es' ? 'Estamos configurando tu experiencia…' : "We're configuring your experience…"}
          </p>
        </motion.div>
      </div>
    );
  }

  /* ── Main layout (Cal AI style) ──────────────────────────────── */
  return (
    <div className="relative min-h-screen flex flex-col text-[#1A211E] pt-safe pb-safe" style={{
      backgroundColor: '#F4EFE8',
    }}>
      {authCfg.loading ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center space-y-4">
            <div className="w-8 h-8 border-2 border-stone-300 border-t-stone-600 rounded-full animate-spin mx-auto" />
            <p className="text-stone-900">{t('common.loading', 'Loading...')}</p>
          </div>
        </div>
      ) : (
        <>
          {/* Logo header for welcome step */}
          {/* Header: back + progress + skip */}
          {showHeader && (
            <div className="flex-shrink-0 px-5 pt-4 pb-2 max-w-md mx-auto w-full">
              <div className="flex items-center gap-3">
                {currentStep > 0 ? (
                  <button onClick={handleBack} className="h-10 w-10 flex items-center justify-center text-[#8E8E8E] hover:text-[#FFFFFF] rounded-full hover:bg-[#1A1A1F] transition-all">
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                ) : <div className="w-10" />}
                <div className="flex-1 flex gap-[3px]">
                  {Array.from({ length: totalSteps }).map((_, i) => (
                    <div key={i} className={`h-[3px] flex-1 rounded-full transition-all duration-300 ${i <= currentStep ? 'bg-[#E36A55]' : 'bg-[#2E2E36]'}`} />
                  ))}
                </div>
                {canSkip ? (
                  <button onClick={handleNext} className="h-10 px-3 text-sm text-[#A9A9A9] hover:text-[#ffffff] font-medium">
                    Skip
                  </button>
                ) : <div className="w-10" />}
              </div>
            </div>
          )}

          {/* Content */}
          <div className={`flex-1 flex flex-col w-full ${
            currentStep === 5 ? 'overflow-hidden' :
            currentStep === 21 ? 'px-6 max-w-md mx-auto' :
            'px-6 max-w-md mx-auto pt-4 overflow-y-auto'
          }`}>
            <AnimatePresence mode="wait">
              <motion.div
                key={currentStep}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
                className="flex-1 flex flex-col"
              >
                {renderStep()}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Bottom buttons */}
          {currentStep !== 5 && currentStep < 19 && (
            <div className="flex-shrink-0 px-6 pb-6 pt-3 max-w-md mx-auto w-full">
              <button
                onClick={handleNext}
                disabled={!isStepValid() || saving}
                className="w-full h-14 font-semibold transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ background: 'linear-gradient(to right, #E36A55, #D35C48)', color: '#ffffff', boxShadow: '0 0 20px rgba(227, 106, 85, 0.25)', borderRadius: '10px' }}
              >
                {lang === 'it' ? 'Continua' : lang === 'es' ? 'Continuar' : 'Continue'}
              </button>
            </div>
          )}

          {currentStep === 19 && outcomeRevealed && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex-shrink-0 px-6 pb-6 pt-3 max-w-md mx-auto w-full">
              <button onClick={handleNext} className="w-full h-14 font-semibold transition-all active:scale-95" style={{ background: 'linear-gradient(to right, #E36A55, #D35C48)', color: '#ffffff', boxShadow: '0 0 20px rgba(227, 106, 85, 0.25)', borderRadius: '10px' }}>
                {lang === 'it' ? 'Continua' : lang === 'es' ? 'Continuar' : 'Continue'}
              </button>
            </motion.div>
          )}

          {currentStep === 20 && (
            <div className="flex-shrink-0 px-6 pb-6 pt-3 max-w-md mx-auto w-full">
              <button
                onClick={handleNext}
                disabled={!isStepValid()}
                className="w-full h-14 font-semibold transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ background: 'linear-gradient(to right, #E36A55, #D35C48)', color: '#ffffff', boxShadow: '0 0 20px rgba(227, 106, 85, 0.25)', borderRadius: '10px' }}
              >
                {lang === 'it' ? 'Continua' : lang === 'es' ? 'Continuar' : 'Continue'}
              </button>
            </div>
          )}

          {currentStep === 21 && personalizationDone && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="flex-shrink-0 px-6 pb-6 pt-3 max-w-md mx-auto w-full">
              <button onClick={handleNext} className="w-full h-14 font-semibold transition-all active:scale-95" style={{ background: 'linear-gradient(to right, #E36A55, #D35C48)', color: '#ffffff', boxShadow: '0 0 20px rgba(227, 106, 85, 0.25)', borderRadius: '10px' }}>
                {lang === 'it' ? 'Continua' : lang === 'es' ? 'Continuar' : 'Continue'}
              </button>
            </motion.div>
          )}

          {currentStep === 22 && (
            <div className="flex-shrink-0 px-6 pb-4 pt-3 max-w-md mx-auto w-full">
              <button
                onClick={handleNext}
                className="w-full h-14 font-semibold transition-all active:scale-95"
                style={{ background: 'linear-gradient(to right, #E36A55, #D35C48)', color: '#ffffff', boxShadow: '0 0 20px rgba(227, 106, 85, 0.25)', borderRadius: '10px' }}
              >
                {lang === 'it' ? 'Continua' : lang === 'es' ? 'Continuar' : 'Continue'}
              </button>
            </div>
          )}

          {currentStep === 23 && (
            <div className="flex-shrink-0 px-6 pb-4 pt-3 max-w-md mx-auto w-full">
              <button
                onClick={handleNext}
                className="w-full h-14 font-semibold transition-all active:scale-95"
                style={{ background: 'linear-gradient(to right, #E36A55, #D35C48)', color: '#ffffff', boxShadow: '0 0 20px rgba(227, 106, 85, 0.25)', borderRadius: '10px' }}
              >
                {lang === 'it' ? 'Continua' : lang === 'es' ? 'Continuar' : 'Continue'}
              </button>
            </div>
          )}

          {currentStep === 24 && (
            <div className="flex-shrink-0 px-6 pb-6 pt-3 max-w-md mx-auto w-full">
              {/* Billing disclosure — above CTA per EU Consumer Rights Directive / Codice del Consumo.
                  One charge, stated plainly: no trial end date, no renewal
                  date, nothing to cancel. */}
              <div className="mb-2 px-3 py-2 rounded-xl" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}>
                <p className="text-[12px] text-[#1A211E] text-center font-medium leading-snug">
                  {lang === 'it'
                    ? `Pagamento unico${lifetimePrice ? ` di ${lifetimePrice}` : ''} · Accesso a vita · Nessun rinnovo`
                    : lang === 'es'
                    ? `Pago único${lifetimePrice ? ` de ${lifetimePrice}` : ''} · Acceso de por vida · Sin renovación`
                    : `One-time payment${lifetimePrice ? ` of ${lifetimePrice}` : ''} · Lifetime access · No renewal`}
                </p>
              </div>

              <button
                onClick={isIAP ? handleIAPCheckout : handleStripeCheckout}
                disabled={checkoutLoading || saving || iapPurchasing || (isIAP && (iapLoading || iapProducts.length === 0)) || !lifetimePrice}
                className="w-full h-14 font-semibold transition-all active:scale-95 disabled:opacity-50"
                style={{ background: 'linear-gradient(to right, #E36A55, #D35C48)', color: '#ffffff', boxShadow: '0 0 20px rgba(227, 106, 85, 0.25)', borderRadius: '10px' }}>
                {(checkoutLoading || iapPurchasing)
                  ? (lang === 'it' ? 'Elaborazione…' : lang === 'es' ? 'Procesando…' : 'Processing…')
                  : (isIAP && iapLoading)
                    ? (lang === 'it' ? 'Caricamento…' : lang === 'es' ? 'Cargando…' : 'Loading…')
                    : (lang === 'it' ? 'Sblocca a vita' : lang === 'es' ? 'Desbloquear de por vida' : 'Unlock for life')}
              </button>
              {isIAP && !iapLoading && iapProducts.length === 0 && (
                <div className="text-center mt-2">
                  <p className="text-[11px] text-amber-400">
                    {lang === 'it'
                      ? 'Impossibile caricare i piani. Controlla la connessione e riprova.'
                      : lang === 'es'
                      ? 'No se pueden cargar los planes. Comprueba tu conexión e inténtalo de nuevo.'
                      : 'Unable to load plans. Check your connection and try again.'}
                  </p>
                  <button
                    type="button"
                    onClick={iapRefresh}
                    className="text-[11px] text-[#3284f1] mt-1 underline"
                  >
                    {lang === 'it' ? 'Riprova' : lang === 'es' ? 'Reintentar' : 'Retry'}
                  </button>
                </div>
              )}
              {isIAP && (
                <button
                  type="button"
                  onClick={handleRestorePurchases}
                  className="text-[11px] text-[#3284f1] text-center mt-2 underline w-full"
                >
                  {lang === 'it' ? 'Ripristina acquisti' : lang === 'es' ? 'Restaurar compras' : 'Restore Purchases'}
                </button>
              )}
              <p className="text-[11px] text-stone-400 text-center mt-2">
                <button type="button" onClick={() => setLegalModal('terms')} className="underline">
                  {lang === 'it' ? 'Termini di Servizio' : lang === 'es' ? 'Términos de Servicio' : 'Terms of Use'}
                </button>
                {' · '}
                <button type="button" onClick={() => setLegalModal('privacy')} className="underline">
                  {lang === 'it' ? 'Privacy Policy' : lang === 'es' ? 'Política de Privacidad' : 'Privacy Policy'}
                </button>
              </p>
            </div>
          )}
        </>
      )}

      {/* ── Legal Bottom-Sheet Modal ─────────────────────────────── */}
      <AnimatePresence>
        {legalModal && (
          <LegalBottomSheet
            type={legalModal}
            lang={lang}
            onClose={() => setLegalModal(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

/* ── LegalBottomSheet component ──────────────────────────────────── */
const TERMS_IT = [
  { title: "1. Accettazione dei Termini", content: "Utilizzando l'applicazione Better Husband (\"App\"), accetti di essere vincolato da questi Termini di Servizio. Se non accetti questi termini, non utilizzare l'App." },
  { title: "2. Descrizione del Servizio", content: "Better Husband è un'applicazione progettata per aiutare gli utenti a migliorare le loro relazioni attraverso suggerimenti personalizzati generati dall'intelligenza artificiale. L'App offre:", list: ["Suggerimenti giornalieri personalizzati","Funzionalità di note e diario","Gestione eventi e promemoria","Statistiche e tracciamento dei progressi"] },
  { title: "3. Account Utente", content: "Per utilizzare l'App, devi creare un account fornendo informazioni accurate e complete. Sei responsabile di:", list: ["Mantenere la riservatezza delle tue credenziali","Tutte le attività che si verificano sotto il tuo account","Notificarci immediatamente di qualsiasi uso non autorizzato"] },
  { title: "4. Abbonamento Premium", content: "Alcune funzionalità dell'App richiedono un abbonamento Premium. Gli abbonamenti si rinnovano automaticamente a meno che non vengano annullati almeno 24 ore prima della fine del periodo corrente. I prezzi possono variare in base alla regione e alla valuta." },
  { title: "5. Contenuti dell'Utente", content: "Mantieni la proprietà di tutti i contenuti che crei nell'App (note, feedback, ecc.). Concedi a Better Husband una licenza limitata per utilizzare questi contenuti al fine di fornire e migliorare il servizio." },
  { title: "6. Uso Accettabile", content: "Accetti di non:", list: ["Utilizzare l'App per scopi illegali","Tentare di accedere ad account di altri utenti","Interferire con il funzionamento dell'App","Utilizzare l'App per molestare o danneggiare altri"] },
  { title: "7. Limitazioni dell'IA", content: "I suggerimenti generati dall'IA sono intesi come guida generale e non sostituiscono la consulenza professionale. Non garantiamo che i suggerimenti siano adatti alla tua situazione specifica. Per problemi seri nella relazione, consigliamo di consultare un professionista." },
  { title: "8. Limitazione di Responsabilità", content: "L'App viene fornita \"così com'è\" senza garanzie di alcun tipo. Non siamo responsabili per danni indiretti, incidentali o consequenti derivanti dall'uso dell'App." },
  { title: "9. Modifiche ai Termini", content: "Possiamo modificare questi termini in qualsiasi momento. Le modifiche saranno effettive al momento della pubblicazione nell'App. L'uso continuato dell'App dopo le modifiche costituisce accettazione dei nuovi termini." },
  { title: "10. Legge Applicabile", content: "Questi termini sono regolati dalle leggi italiane. Qualsiasi controversia sarà sottoposta alla giurisdizione esclusiva dei tribunali italiani." },
  { title: "11. Contatti", content: "Per domande sui Termini di Servizio, contattaci a: support.betterhusband@gmail.com" },
];
const TERMS_EN = [
  { title: "1. Acceptance of Terms", content: "By using the Better Husband application (\"App\"), you agree to be bound by these Terms of Service. If you do not accept these terms, do not use the App." },
  { title: "2. Service Description", content: "Better Husband is an application designed to help users improve their relationships through personalized AI-generated suggestions. The App offers:", list: ["Personalized daily suggestions","Notes and journal features","Event management and reminders","Statistics and progress tracking"] },
  { title: "3. User Account", content: "To use the App, you must create an account by providing accurate and complete information. You are responsible for:", list: ["Maintaining the confidentiality of your credentials","All activities that occur under your account","Notifying us immediately of any unauthorized use"] },
  { title: "4. Premium Subscription", content: "Some App features require a Premium subscription. Subscriptions automatically renew unless canceled at least 24 hours before the end of the current period. Prices may vary by region and currency." },
  { title: "5. User Content", content: "You retain ownership of all content you create in the App (notes, feedback, etc.). You grant Better Husband a limited license to use this content to provide and improve the service." },
  { title: "6. Acceptable Use", content: "You agree not to:", list: ["Use the App for illegal purposes","Attempt to access other users' accounts","Interfere with the App's operation","Use the App to harass or harm others"] },
  { title: "7. AI Limitations", content: "AI-generated suggestions are intended as general guidance and do not replace professional advice. We do not guarantee that suggestions are suitable for your specific situation. For serious relationship issues, we recommend consulting a professional." },
  { title: "8. Limitation of Liability", content: "The App is provided \"as is\" without warranties of any kind. We are not liable for indirect, incidental, or consequential damages arising from use of the App." },
  { title: "9. Changes to Terms", content: "We may modify these terms at any time. Changes will be effective upon posting in the App. Continued use of the App after changes constitutes acceptance of the new terms." },
  { title: "10. Governing Law", content: "These terms are governed by Italian law. Any disputes shall be subject to the exclusive jurisdiction of Italian courts." },
  { title: "11. Contact", content: "For questions about the Terms of Service, contact us at: support.betterhusband@gmail.com" },
];
const PRIVACY_IT = [
  { title: "1. Introduzione", content: "Better Husband (\"noi\", \"nostro\" o \"Applicazione\") si impegna a proteggere la tua privacy. Questa Privacy Policy spiega come raccogliamo, utilizziamo e proteggiamo le tue informazioni personali quando utilizzi la nostra applicazione mobile e i nostri servizi." },
  { title: "2. Informazioni che Raccogliamo", content: "Raccogliamo le seguenti categorie di informazioni:", list: ["Informazioni del profilo: nome, email, preferenze linguistiche","Dati sulla relazione: durata della relazione, numero di figli, livelli di comunicazione e stress","Dati di utilizzo: suggerimenti visualizzati, feedback forniti, check-in completati","Note personali: appunti che scegli di salvare nell'app","Dati di pagamento: elaborati in modo sicuro da Stripe (non conserviamo dati delle carte)"] },
  { title: "3. Come Utilizziamo le Tue Informazioni", content: "Utilizziamo le informazioni raccolte per:", list: ["Personalizzare i suggerimenti giornalieri in base al tuo profilo","Migliorare l'algoritmo di raccomandazione","Inviarti notifiche e promemoria (se abilitati)","Elaborare pagamenti per funzionalità premium","Migliorare e ottimizzare l'applicazione"] },
  { title: "4. Condivisione delle Informazioni", content: "Non vendiamo, affittiamo o condividiamo le tue informazioni personali con terze parti per scopi di marketing. Condividiamo informazioni solo con:", list: ["Provider di servizi (hosting, elaborazione pagamenti) necessari per il funzionamento dell'app","Autorità legali quando richiesto dalla legge","OpenAI, il fornitore di IA che genera i tuoi gesti quotidiani. A questo scopo gli inviamo le risposte che hai dato all’inizio, i tuoi gesti recenti, le note e i check-in che hai scritto, le date che hai aggiunto e, se lo hai inserito, il nome della tua partner. Non inviamo il tuo nome, la tua email né i dati di pagamento."] },
  { title: "5. Analisi e Statistiche d'Uso", content: "Utilizziamo Google Firebase Analytics (Google Ireland Limited) per comprendere come l'App viene utilizzata e migliorarla. Vengono raccolti identificatori dell'installazione, tipo di dispositivo, lingua, eventi di utilizzo aggregati e versione dell'app; non raccogliamo le tue note o dati sensibili. La base giuridica è il consenso (art. 6.1.a GDPR), espresso accettando questa Privacy Policy. Puoi revocare il consenso in qualsiasi momento scrivendo a support.betterhusband@gmail.com. I dati possono essere trasferiti fuori dall'UE sulla base delle Clausole Contrattuali Standard." },
  { title: "6. Sicurezza dei Dati", content: "Implementiamo misure di sicurezza tecniche e organizzative per proteggere le tue informazioni, inclusa la crittografia dei dati in transito e a riposo, accesso limitato ai dati personali e monitoraggio regolare della sicurezza." },
  { title: "7. I Tuoi Diritti", content: "Hai il diritto di:", list: ["Accedere ai tuoi dati personali","Richiedere la correzione di dati inesatti","Richiedere la cancellazione dei tuoi dati","Esportare i tuoi dati in formato leggibile","Revocare il consenso in qualsiasi momento"] },
  { title: "8. Conservazione dei Dati", content: "Conserviamo i tuoi dati personali per tutto il tempo in cui il tuo account è attivo o necessario per fornirti i servizi. Puoi richiedere la cancellazione del tuo account e dei dati associati in qualsiasi momento contattandoci." },
  { title: "9. Contatti", content: "Per domande sulla privacy o per esercitare i tuoi diritti, contattaci a: support.betterhusband@gmail.com" },
];
const PRIVACY_EN = [
  { title: "1. Introduction", content: "Better Husband (\"we\", \"our\" or \"Application\") is committed to protecting your privacy. This Privacy Policy explains how we collect, use and protect your personal information when you use our mobile application and services." },
  { title: "2. Information We Collect", content: "We collect the following categories of information:", list: ["Profile information: name, email, language preferences","Relationship data: relationship duration, number of children, communication and stress levels","Usage data: suggestions viewed, feedback provided, check-ins completed","Personal notes: notes you choose to save in the app","Payment data: securely processed by Stripe (we do not store card data)"] },
  { title: "3. How We Use Your Information", content: "We use the collected information to:", list: ["Personalize daily suggestions based on your profile","Improve the recommendation algorithm","Send you notifications and reminders (if enabled)","Process payments for premium features","Improve and optimize the application"] },
  { title: "4. Information Sharing", content: "We do not sell, rent or share your personal information with third parties for marketing purposes. We only share information with:", list: ["Service providers (hosting, payment processing) necessary for app operation","Legal authorities when required by law","OpenAI, the AI provider that generates your daily gestures. To do this we send it the answers you gave at setup, your recent gestures, the notes and check-ins you wrote, the dates you added and, if you entered it, your partner’s first name. We do not send your name, email address or payment details."] },
  { title: "5. Analytics & Usage Statistics", content: "We use Google Firebase Analytics (Google Ireland Limited) to understand how the App is used and to improve it. We collect installation identifiers, device type, language, aggregated usage events and app version; we do not collect the content of your notes or sensitive data. The legal basis is your consent (Art. 6.1.a GDPR), expressed by accepting this Privacy Policy. You can withdraw consent at any time by writing to support.betterhusband@gmail.com. Data may be transferred outside the EU under the European Commission's Standard Contractual Clauses." },
  { title: "6. Data Security", content: "We implement technical and organizational security measures to protect your information, including encryption of data in transit and at rest, limited access to personal data, and regular security monitoring." },
  { title: "7. Your Rights", content: "You have the right to:", list: ["Access your personal data","Request correction of inaccurate data","Request deletion of your data","Export your data in readable format","Withdraw consent at any time"] },
  { title: "8. Data Retention", content: "We retain your personal data for as long as your account is active or as needed to provide you with services. You can request deletion of your account and associated data at any time by contacting us." },
  { title: "9. Contact", content: "For privacy questions or to exercise your rights, contact us at: support.betterhusband@gmail.com" },
];
const TERMS_ES = [
  { title: "1. Aceptación de los Términos", content: "Al utilizar la aplicación Better Husband (\"Aplicación\"), aceptas quedar vinculado por estos Términos de Servicio. Si no aceptas estos términos, no utilices la Aplicación." },
  { title: "2. Descripción del Servicio", content: "Better Husband es una aplicación diseñada para ayudar a los usuarios a mejorar sus relaciones mediante sugerencias personalizadas generadas por inteligencia artificial. La Aplicación ofrece:", list: ["Sugerencias diarias personalizadas","Funciones de notas y diario","Gestión de eventos y recordatorios","Estadísticas y seguimiento del progreso"] },
  { title: "3. Cuenta de Usuario", content: "Para utilizar la Aplicación, debes crear una cuenta proporcionando información precisa y completa. Eres responsable de:", list: ["Mantener la confidencialidad de tus credenciales","Todas las actividades realizadas con tu cuenta","Notificarnos de inmediato cualquier uso no autorizado"] },
  { title: "4. Suscripción Premium", content: "Algunas funciones de la Aplicación requieren una suscripción Premium. Las suscripciones se renuevan automáticamente a menos que se cancelen al menos 24 horas antes del final del periodo en curso. Los precios pueden variar según la región y la moneda." },
  { title: "5. Contenido del Usuario", content: "Conservas la propiedad de todo el contenido que crees en la Aplicación (notas, comentarios, etc.). Concedes a Better Husband una licencia limitada para utilizar dicho contenido con el fin de prestar y mejorar el servicio." },
  { title: "6. Uso Aceptable", content: "Aceptas no:", list: ["Utilizar la Aplicación con fines ilegales","Intentar acceder a cuentas de otros usuarios","Interferir con el funcionamiento de la Aplicación","Utilizar la Aplicación para acosar o dañar a otros"] },
  { title: "7. Limitaciones de la IA", content: "Las sugerencias generadas por IA están pensadas como orientación general y no sustituyen el asesoramiento profesional. No garantizamos que las sugerencias sean adecuadas para tu situación específica. Para problemas serios en la relación, recomendamos consultar a un profesional." },
  { title: "8. Limitación de Responsabilidad", content: "La Aplicación se proporciona \"tal cual\", sin garantías de ningún tipo. No somos responsables de daños indirectos, incidentales o consecuentes derivados del uso de la Aplicación." },
  { title: "9. Modificaciones de los Términos", content: "Podemos modificar estos términos en cualquier momento. Los cambios serán efectivos desde su publicación en la Aplicación. El uso continuado de la Aplicación tras los cambios constituye la aceptación de los nuevos términos." },
  { title: "10. Ley Aplicable", content: "Estos términos se rigen por la legislación italiana. Cualquier controversia se someterá a la jurisdicción exclusiva de los tribunales italianos." },
  { title: "11. Contacto", content: "Para preguntas sobre los Términos de Servicio, contáctanos en: support.betterhusband@gmail.com" },
];
const PRIVACY_ES = [
  { title: "1. Introducción", content: "Better Husband (\"nosotros\", \"nuestro\" o \"Aplicación\") se compromete a proteger tu privacidad. Esta Política de Privacidad explica cómo recopilamos, utilizamos y protegemos tu información personal cuando utilizas nuestra aplicación móvil y nuestros servicios." },
  { title: "2. Información que Recopilamos", content: "Recopilamos las siguientes categorías de información:", list: ["Información del perfil: nombre, correo electrónico, preferencias de idioma","Datos de la relación: duración de la relación, número de hijos, niveles de comunicación y estrés","Datos de uso: sugerencias vistas, comentarios proporcionados, check-ins completados","Notas personales: notas que decides guardar en la aplicación","Datos de pago: procesados de forma segura por Stripe (no almacenamos datos de tarjetas)"] },
  { title: "3. Cómo Utilizamos tu Información", content: "Utilizamos la información recopilada para:", list: ["Personalizar las sugerencias diarias según tu perfil","Mejorar el algoritmo de recomendación","Enviarte notificaciones y recordatorios (si están habilitados)","Procesar pagos por funciones premium","Mejorar y optimizar la aplicación"] },
  { title: "4. Compartir Información", content: "No vendemos, alquilamos ni compartimos tu información personal con terceros con fines de marketing. Solo compartimos información con:", list: ["Proveedores de servicios (hosting, procesamiento de pagos) necesarios para el funcionamiento de la app","Autoridades legales cuando lo exija la ley","OpenAI, el proveedor de IA que genera tus gestos diarios. Para ello le enviamos las respuestas que diste al empezar, tus gestos recientes, las notas y los repasos que escribiste, las fechas que añadiste y, si lo indicaste, el nombre de tu pareja. No enviamos tu nombre, tu correo electrónico ni tus datos de pago."] },
  { title: "5. Analítica y Estadísticas de Uso", content: "Utilizamos Google Firebase Analytics (Google Ireland Limited) para entender cómo se usa la Aplicación y mejorarla. Recopilamos identificadores de instalación, tipo de dispositivo, idioma, eventos de uso agregados y versión de la app; no recopilamos el contenido de tus notas ni datos sensibles. La base jurídica es tu consentimiento (art. 6.1.a RGPD), expresado al aceptar esta Política de Privacidad. Puedes retirar el consentimiento en cualquier momento escribiendo a support.betterhusband@gmail.com. Los datos pueden transferirse fuera de la UE conforme a las Cláusulas Contractuales Tipo de la Comisión Europea." },
  { title: "6. Seguridad de los Datos", content: "Implementamos medidas de seguridad técnicas y organizativas para proteger tu información, incluido el cifrado de los datos en tránsito y en reposo, el acceso limitado a los datos personales y la supervisión periódica de la seguridad." },
  { title: "7. Tus Derechos", content: "Tienes derecho a:", list: ["Acceder a tus datos personales","Solicitar la corrección de datos inexactos","Solicitar la eliminación de tus datos","Exportar tus datos en un formato legible","Retirar el consentimiento en cualquier momento"] },
  { title: "8. Conservación de los Datos", content: "Conservamos tus datos personales mientras tu cuenta esté activa o sea necesario para prestarte los servicios. Puedes solicitar la eliminación de tu cuenta y de los datos asociados en cualquier momento contactándonos." },
  { title: "9. Contacto", content: "Para preguntas sobre privacidad o para ejercer tus derechos, contáctanos en: support.betterhusband@gmail.com" },
];

const LegalBottomSheet = ({ type, lang, onClose }) => {
  const sheetRef = useRef(null);
  const dragStartY = useRef(0);
  const currentTranslateY = useRef(0);

  const isTerms = type === 'terms';
  const sections = isTerms
    ? (lang === 'it' ? TERMS_IT : lang === 'es' ? TERMS_ES : TERMS_EN)
    : (lang === 'it' ? PRIVACY_IT : lang === 'es' ? PRIVACY_ES : PRIVACY_EN);
  const title = isTerms
    ? (lang === 'it' ? 'Termini di Servizio' : lang === 'es' ? 'Términos de Servicio' : 'Terms of Service')
    : (lang === 'it' ? 'Privacy Policy' : lang === 'es' ? 'Política de Privacidad' : 'Privacy Policy');
  const lastUpdated = isTerms
    ? (lang === 'it' ? 'Ultimo aggiornamento: 2 Gennaio 2025' : lang === 'es' ? 'Última actualización: 2 de enero de 2025' : 'Last updated: January 2, 2025')
    : (lang === 'it' ? 'Ultimo aggiornamento: 30 Settembre 2026' : lang === 'es' ? 'Última actualización: 30 de septiembre de 2026' : 'Last updated: September 30, 2026');

  const onTouchStart = (e) => {
    dragStartY.current = e.touches[0].clientY;
    currentTranslateY.current = 0;
    if (sheetRef.current) sheetRef.current.style.transition = 'none';
  };
  const onTouchMove = (e) => {
    const dy = e.touches[0].clientY - dragStartY.current;
    if (dy > 0) {
      currentTranslateY.current = dy;
      if (sheetRef.current) sheetRef.current.style.transform = `translateY(${dy}px)`;
    }
  };
  const onTouchEnd = () => {
    if (sheetRef.current) sheetRef.current.style.transition = 'transform 0.3s ease';
    if (currentTranslateY.current > 100) {
      onClose();
    } else {
      if (sheetRef.current) sheetRef.current.style.transform = 'translateY(0)';
    }
  };

  return (
    <>
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        onClick={onClose}
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', zIndex: 9998 }}
      />
      {/* Sheet */}
      <motion.div
        ref={sheetRef}
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ type: 'spring', damping: 28, stiffness: 300 }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        style={{
          position: 'fixed', bottom: 0, left: 0, right: 0,
          maxHeight: '85vh', zIndex: 9999,
          background: 'hsl(var(--background))', borderRadius: '20px 20px 0 0',
          display: 'flex', flexDirection: 'column',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
          border: '1px solid rgba(0,0,0,0.08)',
          boxShadow: '0 -20px 40px rgba(0,0,0,0.08)',
        }}
      >
        {/* Drag handle */}
        <div style={{ display: 'flex', justifyContent: 'center', padding: '12px 0 4px' }}>
          <div style={{ width: 40, height: 4, borderRadius: 2, background: 'rgba(255,255,255,0.3)' }} />
        </div>
        {/* Header */}
        <div style={{ padding: '8px 24px 12px', borderBottom: '1px solid rgba(0,0,0,0.08)', flexShrink: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2 style={{ fontSize: 20, fontWeight: 700, color: '#000', margin: 0 }}>{title}</h2>
            <button onClick={onClose} style={{ background: 'rgba(0,0,0,0.06)', border: 'none', borderRadius: '50%', width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
              <svg viewBox="0 0 24 24" style={{ width: 16, height: 16, stroke: '#000', fill: 'none', strokeWidth: 2 }}><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>
          <p style={{ fontSize: 12, color: 'rgba(0,0,0,0.6)', marginTop: 4 }}>{lastUpdated}</p>
        </div>
        {/* Scrollable content */}
        <div style={{ overflowY: 'auto', padding: '20px 24px 32px', flex: 1, WebkitOverflowScrolling: 'touch' }}>
          {sections.map((section, index) => (
            <div key={index} style={{ marginBottom: 24 }}>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: '#000', marginBottom: 8 }}>{section.title}</h3>
              <p style={{ fontSize: 14, lineHeight: 1.6, color: 'rgba(0,0,0,0.8)' }}>{section.content}</p>
              {section.list && (
                <ul style={{ marginTop: 8, paddingLeft: 0, listStyle: 'none' }}>
                  {section.list.map((item, i) => (
                    <li key={i} style={{ fontSize: 14, color: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 6 }}>
                      <span style={{ color: '#5C8748', marginTop: 2 }}>•</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      </motion.div>
    </>
  );
};

export default OnboardingV2;
